import axios from "./axios-config";
import { askVirtualPlace, isVirtualLocationRequired, virtualPlaceChoices } from '@/features/task-expense/virtualPlaceChoice';
import { invalidateProductPackInfo } from '@/features/asset-mgr/purchaseOptionCache';
import { emitEntityChanged } from "@/lib/entityChangeBus";
import { workflowPayloadToBackend, workflowResponseFromBackend } from "./workflow-transform";

// 导入类型定义
import type {
  TaskExpense,
  TaskExpenseAction,
  TaskExpenseCreatePayload,
  TaskExpensePatchPayload,
} from "@/features/task-expense/types";
import type { SnowflakeId } from "@/types/snowflake";
import type { HouseRulesAgreementHistory } from "@/types/house-rules-agreement";
import type { ReservationAction, GateAccessStatusResponse } from "@/types/reservation-action";
import type { GuestFormDetail } from "@/types/guest-form";
import type {
    Listing,
    ListingFieldSync,
    ListingFieldWriteBackResult,
    ListingPhotoAnalysis,
    ListingPhotoAnalysisState,
    ListingPhotoSummary,
    ListingPhotoList,
    ListingPhotoSyncPlan,
    ListingPhotoSyncResult,
    ListingMarketingInfo,
} from "@/types/listing";
import type { Reservation, CreateReservationInput } from "@/types/reservation";
import type { Guest } from "@/types/guest";
import type { ReviewWithDetails } from "@/types/review";
import type { GuestGuideApiResponse, GuestGuideUpdateRequest } from "@/types/guest-guide";
import type {
    TriggerExportRequest,
    GetExportHistoryParams,
    ExportHistoryOutput,
    ExportHistoryListResponse,
} from "@/types/export-history";
import type { ApiKeyCreateRequest, ApiKeyCreateOutput } from "@/types/api-key";
import type { AccessCodeGenerateRequest, AccessCodeOutput } from "@/types/access-code";
import type {
    Purchase,
    PurchaseActionType,
    PurchaseReceiveItem,
    PurchaseReturnItem,
    PurchaseCancelItem,
    PurchaseReconcileItem,
    PurchaseCorrectReceiptItem,
    PurchaseCreateItem,
    PurchaseUpdatePayload,
    PurchaseActionResponse,
    PurchaseBatchCreateResponse,
} from "@/features/procurement/types";

import type { AssetLinkRef, Task, TaskUpdateBody } from "@/types/task";
import type {
  AssetDTO,
  AssetDeleteBatchResponse,
  AssetDeletePreviewResponse,
} from "@/types/asset";
import type { SearchResponse } from "@/features/list-page/types";
import type { Intent } from "@/types/intent";
import type { View } from "@/types/view";
import type { Owner, OwnerListing, OwnerPortalAccount, OwnerPortalAccountCreate } from "@/features/owner/types/owner";
import type { Tenant } from "@/types/tenant";
import type {
  AiReplyAskInput,
  AiReplyReviewInput,
  AiReplySession,
  AiReplySessionCreateInput,
  AiReplySessionDetail,
  AiReplyTurn,
} from "@/features/ai-test-chat/types";
import type { AiReplyInboxRequest, InboxAiTurn } from "@/features/inbox-ai-test/types";

// Re-export types for backwards compatibility
export type { PaginatedResponse, ReviewParams } from "@/types/common";
export type { GuestGuideApiResponse, GuestGuideUpdateRequest } from "@/types/guest-guide";
export type {
    TriggerExportRequest,
    ExportHistoryItem,
    ExportHistoryOutput,
    ExportHistoryListResponse,
    GetExportHistoryParams,
} from "@/types/export-history";

// Reviews API
export const reviewsApi = {
    getReview: (id: string) =>
        // cleaner/inspector 已并入 review 主表(后端删除 review_insight 表),
        // 直接作为关系加载,不再走旧的 insight / insight.cleaner / insight.inspector。
        axios.get<any, ReviewWithDetails>(
            `/pms/reviews/${id}?load_relationships=listing&load_relationships=reservation&load_relationships=guest&load_relationships=cleaner&load_relationships=inspector`,
        ),
    searchReviews: (data: any) => axios.post("/pms/reviews/search", data),
    // 回复先检查再发（后端 RV12）：空、超长、照抄私密评价的回复一律 400 REVIEW_REPLY_BLOCKED；有提醒时
    // confirmWarnings=false 返回 400 REVIEW_REPLY_NEEDS_CONFIRMATION（details.warnings），让人确认后带 true 重发。
    // logComment=true：发布成功后由后端写"谁回复了"的评论（评价详情页用，前端不再自己写）。不传 = 后端原默认（直接发、不写评论）。
    replyToReview: (id: string, replyText: string, opts?: { confirmWarnings?: boolean; logComment?: boolean }) =>
        axios.put(`/pms/reviews/${id}/reply`, {
            reply_text: replyText,
            auto_translate: false,
            confirm_warnings: opts?.confirmWarnings ?? true,
            log_comment: opts?.logComment ?? false,
        }),
    // AI 分析：上下文由后端组装；已有结果直接返回（served=store），force=true 重算
    analyzeReview: (id: string, force = false) =>
        axios.post(`/pms/reviews/${id}/analyze`, { force }),
    assignPicReviews: (params: any) =>
        axios.patch(`/pms/reviews/assign-pic`, params),
    batchDeleteReview: (params: { ids: string[] }) =>
        axios.post("/pms/reviews/batch-delete", params),
    updateReviewStatus: (id: string, status: string) =>
        axios.put(`/pms/reviews/${id}`, { reviewStatus: status }),
};

// Listings API
export const listingsApi = {
    searchListing: (params: any) => axios.post("/pms/listings/search", params),
    batchUpdateListing: (params: any) =>
        axios.patch("/pms/listings/batch-update-tags-and-group", params),
    batchDeleteListing: (params: { ids: string[] }) =>
        axios.post("/pms/listings/batch-delete", params),
    // `config` 是 axios 请求配置，例：调用方自己显示 404 时传 `{ suppressNotFoundToast: true }`
    getListing: (id: string, params: any, config?: Record<string, unknown>) => {
        // 构建查询参数
        const queryParams = new URLSearchParams();

        // 处理load_relationships参数
        if (params?.load_relationships) {
            const relationships = Array.isArray(params.load_relationships)
                ? params.load_relationships
                : [params.load_relationships];

            relationships.forEach((rel: string) => {
                queryParams.append("load_relationships", rel);
            });
        }

        // 处理其他参数
        if (params) {
            Object.keys(params).forEach((key) => {
                if (key !== "load_relationships" && params[key] !== undefined) {
                    queryParams.append(key, params[key]);
                }
            });
        }

        const queryString = queryParams.toString();
        const url = `/pms/listings/${id}${queryString ? `?${queryString}` : ""}`;

        return axios.get<any, Listing>(url, config);
    },
    getListingLookup: (params: any) =>
        axios.post(`/pms/listings/lookup`, params),
    // `config` is the axios request config — e.g. `{ suppressNotFoundToast: true }`
    // when the caller renders the failure itself (see axios-config.ts 404 branch).
    /** 我的虚拟地点。本人可用，不要 asset-mgr:location:read */
    getMyVirtualLocations: () => axios.get("/pms/listings/me/virtual-locations"),
    createListing: (data: any) => axios.post("/pms/listings", data),
    deleteListing: (id: string) => axios.delete(`/pms/listings/${id}`),
    addLocationUser: (id: string, userId: string) =>
        axios.post(`/pms/listings/${id}/location-users`, { userIds: [userId] }),
    removeLocationUser: (id: string, userId: string) =>
        axios.delete(`/pms/listings/${id}/location-users`, { data: { userIds: [userId] } }),
    updateListing: (id: string, data: any, config?: Record<string, unknown>) =>
        axios.put<any, Listing>(`/pms/listings/${id}`, data, config),
    // Marketing copy (summary/space/access/…/houseRules). Backend endpoints exist;
    // getListing already returns `marketingInfo` inline, so read from the detail
    // object and only call these when editing. No language_code on this resource.
    // 404 = 这套房还没有营销信息行，是正常业务态（PMS 导入进来的房源走 batch_upsert，
    // 不走会自动建空行的 ListingService.create，marketing 行只能等同步带过来）。
    // MarketingSection 自己把它渲染成「还没有营销信息」。不带 suppressNotFoundToast
    // 的话，全局拦截器还会再弹一条红色 `Marketing info not found` —— 页面明明优雅
    // 处理了，运营看到的却是「点营销 tab 就报错」（2026-10-24 prd 2317LC WIR）。
    getListingMarketingInfo: (id: string) =>
        axios.get<any, ListingMarketingInfo>(
            `/pms/listings/${id}/marketing-info`,
            { suppressNotFoundToast: true } as any,
        ),
    // ⚠️ 这个端点是 **PMS 同步的入口**，不是后台编辑的入口：八段基础列是 PMS 同步来的，
    // 每次房源同步都被原样覆盖回来。往这里写英文等于白改且没有任何提示（main-api 的
    // ListingService.update_marketing_info 有完整说明）。
    // 后台改英文用 updateListingMarketingEnglish，写译文用 updateListingMarketingTranslations。
    updateListingMarketingInfo: (
        id: string,
        data: Partial<ListingMarketingInfo>,
    ) =>
        axios.put<any, ListingMarketingInfo>(
            `/pms/listings/${id}/marketing-info`,
            data,
        ),
    /**
     * 改英文营销文案。写的是**基础列**，由**字段归属锁**挡住 PMS 同步 —— 2026-10-22（B4）
     * 收掉了 2026-10-13 那套 `*Override` 覆盖列（它只是为躲同步而存在的权宜之计）。
     *
     * `fields` 的键是基础字段名（`summary` / `houseRules` / …，共七段）：
     *
     * - 键不出现 → 不动这一段（它的正文和待重译标记都保持原样）
     * - 值是文本 → 这一段的英文改成它
     * - 值是 `null` → **清空**这一段（不是「恢复跟随 PMS」）
     *
     * 想恢复跟随 PMS 走 `releaseListingFieldLock`（它会把记下的 PMS 原文写回基础列）。
     *
     * ⚠️ 这一段还跟随 PMS、且这家 PMS 写不回正文时，后端回 **409 `PMS_FIELD_LOCKED`**，
     * `details.fields` 是被挡下的锁键。调用方要么先解锁（`unlockListingFields`）、要么像
     * MarketingSection 那样就地弹确认框再重放。
     *
     * 后端的 schema 是 `extra='forbid'` 且只有这七个字段：传 `summaryOverride` 这种旧的
     * 覆盖字段名会 422。改了英文会让该段译文被标成待重译（刻意的）。
     */
    updateListingMarketingEnglish: (
        id: string,
        fields: Record<string, string | null>,
    ) =>
        axios.put<any, ListingMarketingInfo>(
            `/pms/listings/${id}/marketing-info/english`,
            fields,
        ),
    /**
     * 写营销文案的译文。**写不到英文**（后端那条路只收 `translations`）。改英文用
     * `updateListingMarketingEnglish`。
     *
     * `translations` 是 `{字段名: {locale: text}}`，字段名用 camelCase；没传的字段
     * 保持原有译文**和**原有的待重译标记。空白译文 = 取消该语种。
     */
    updateListingMarketingTranslations: (
        id: string,
        translations: Record<string, Record<string, string>>,
    ) =>
        axios.put<any, ListingMarketingInfo>(
            `/pms/listings/${id}/marketing-info/translations`,
            { translations },
        ),

    // ===== 字段归属锁（PMS 同步不覆盖 Nova 改过的字段）=====
    //
    // 没有锁行 = 字段跟随 PMS（默认）；有锁行 = Nova 保留，同步跳过它。
    // 键是**锁键**：`listing.bedrooms`、`marketing.summary`；地址 8 列合成一个
    // `listing.address`（整组解锁、整组恢复）。
    //
    // ⚠️ 这套状态**不要**从房源详情里读：详情回包挂着版本缓存，而锁写入不会 bump 它的
    // 版本 —— 解锁了详情还是旧的。所以单独一个不缓存的接口，锁操作后重拉它。
    getListingFieldLocks: (id: string) =>
        axios.get<any, ListingFieldSync>(`/pms/listings/${id}/field-locks`),
    /** 解锁：这些字段以后不再从 PMS 同步，Nova 保留运营的值。`listing.is_listed` 永远拒绝（400）。 */
    unlockListingFields: (id: string, fields: string[]) =>
        axios.post<any, ListingFieldSync>(`/pms/listings/${id}/field-locks`, {
            fields,
        }),
    /** 把 Nova 保留的值写回 PMS：能写的写回并恢复跟随，这家不支持的在 `unsupported` 里返回。 */
    writeBackListingFieldLocks: (id: string) =>
        axios.post<any, ListingFieldWriteBackResult>(
            `/pms/listings/${id}/field-locks/write-back`,
        ),
    /** 恢复跟随 PMS：值改回 PMS 的值并删锁行。也用于「改用 PMS 的值」。 */
    releaseListingFieldLock: (id: string, field: string) =>
        axios.post<any, ListingFieldSync>(
            `/pms/listings/${id}/field-locks/${encodeURIComponent(field)}/release`,
        ),
    // ── 照片（P1 / P3 / P12 / P19 / P21）─────────────────────────────────────
    //
    // ⚠️ **照片有两套 id。** 这里回的 `photoId` 是**写回能用的那套**（PMS 的照片接口），
    // 而房源详情里 `pictures[]._id` 是另一套：2026-10-24 在 dev 上拿同一张照片的两个身份
    // 对过，只有 ObjectId 的时间戳前缀相同。**编辑照片一律用这个接口的 id**，拿详情里的
    // 去删 / 排序，对方只会说找不到照片。
    //
    // 不缓存：写回之后 PMS 推回 Nova 要 1–12 秒，编辑器必须看当下的。
    //
    // 读不到时页面自己退回只读并说明原因（PhotosSection），所以 404 不再让全局拦截器另弹一条
    // 红色 toast —— 否则「没接 PMS / 这家不支持照片」这种正常态每点一次照片页就报一次错。
    getListingPhotos: (id: string) =>
        axios.get<any, ListingPhotoList>(
            `/pms/listings/${id}/photos`,
            { suppressNotFoundToast: true } as any,
        ),
    /** 这个房源已有的照片 AI 分析（P4）+ 房源级总结（P8）。没分析过的照片不在回包里。 */
    getListingPhotoAnalysis: (id: string) =>
        axios.get<any, ListingPhotoAnalysisState>(`/pms/listings/${id}/photo-analysis`),
    /**
     * 存照片分析 / 房源总结（P4 / P8）。只发要改的那几张，没发的保持原样。
     * **不碰 PMS** —— 真要改 caption / 换图 / 排序走 `syncListingPhotos`。
     */
    saveListingPhotoAnalysis: (id: string, body: {
        analyses?: Partial<ListingPhotoAnalysis>[]
        summary?: Partial<ListingPhotoSummary>
    }) =>
        axios.put<any, ListingPhotoAnalysisState>(`/pms/listings/${id}/photo-analysis`, body),
    /** 一次性把照片草稿写回 PMS：新增 → 替换 → 改 caption → 删除 → 排序。逐项报告，部分失败不回滚。 */
    syncListingPhotos: (id: string, plan: ListingPhotoSyncPlan) =>
        axios.post<any, ListingPhotoSyncResult>(
            `/pms/listings/${id}/photos/sync`,
            plan,
        ),
    /** 忽略 PMS 的新值提示（只清时间戳）。PMS 再改一次还会提示。 */
    dismissListingFieldPmsChange: (id: string, field: string) =>
        axios.post<any, ListingFieldSync>(
            `/pms/listings/${id}/field-locks/${encodeURIComponent(field)}/dismiss`,
        ),
};

// Intent API
export const intentsApi = {
    searchIntent: (params: any) =>
        axios.post<any, SearchResponse<Intent>>("/pms/intents/search", params),
    getIntent: (id: string, loadRelationships: string[] = ['listing', 'tasks', 'attachments', 'assignee', 'creator', 'resolver', 'task_assignee', 'updater']) =>
        axios.get(
            `/pms/intents/${id}?${loadRelationships.map(r => `load_relationships=${r}`).join('&')}`,
        ),
    createIntent: (data: any) => axios.post("/pms/intents", data),
    updateIntent: async (id: string, data: any) => {
        const r = await axios.put(`/pms/intents/${id}`, data);
        emitEntityChanged({ entity: "intent", id });
        return r;
    },
    deleteIntent: (id: string) => axios.delete(`/pms/intents/${id}`),
    batchDeleteIntent: (params: any) =>
        axios.post(`/pms/intents/batch-delete`, params),
    getIntentLookup: (params?: any) =>
        axios.post("/pms/intents/lookup", params),
    IntentEntityLink: async (id: string, params?: any) => {
        const r = await axios.post(`/pms/intents/${id}/entity-links`, params);
        emitEntityChanged({ entity: "intent", id });
        return r;
    },
};

// ============================================================
// Service Requests API (Intent → Request v1) — /pms/service-requests/*
// Physical table is still pms.intent; permission codes reuse pms:intent:*.
// axios interceptor unwraps { code, data, msg } → data.
// ============================================================
export const serviceRequestsApi = {
    /** POST /pms/service-requests/search — standard Search DSL (filters/columns/orderBy/page). */
    search: (params: any) => axios.post("/pms/service-requests/search", params),
    /** GET /pms/service-requests/{id} — detail (listing/reservation/coordinator/attachments/taskLinks/linkedAssets/deliveryItems). */
    get: (id: string) => axios.get(`/pms/service-requests/${id}`),
    /** POST /pms/service-requests — create. Only description required. */
    create: (data: {
        description: string
        listingId?: string | null
        reservationId?: string | null
        category?: string
        priority?: string
        source?: string
        /** 进入渠道（web / inbox / …） */
        channel?: string
        /** 在哪张任务里报出的：后端写「报出自」关系（现场上报，巡检任务的「上报问题」） */
        originTaskId?: string
    }) => axios.post("/pms/service-requests", data),
    /** PUT /pms/service-requests/{id} — partial update (description/category/priority/listingId/reservationId). 409 on terminal. */
    update: async (id: string, data: Record<string, any>) => {
        const r = await axios.put(`/pms/service-requests/${id}`, data);
        emitEntityChanged({ entity: "intent", id });
        return r;
    },
    /** POST /pms/service-requests/{id}/take — claim (assignee = current user, new → in_progress). 409 if already claimed. */
    take: async (id: string) => {
        const r = await axios.post(`/pms/service-requests/${id}/take`, {});
        emitEntityChanged({ entity: "intent", id });
        return r;
    },
    /** POST /pms/service-requests/{id}/resolve — status → resolved. 409 on terminal. */
    resolve: async (id: string, data?: { note?: string }) => {
        const r = await axios.post(`/pms/service-requests/${id}/resolve`, data ?? {});
        emitEntityChanged({ entity: "intent", id });
        return r;
    },
    /** POST /pms/service-requests/{id}/dismiss — status → dismissed with a reason. 409 on terminal. */
    dismiss: async (id: string, data: { reason: string; note?: string }) => {
        const r = await axios.post(`/pms/service-requests/${id}/dismiss`, data);
        emitEntityChanged({ entity: "intent", id });
        return r;
    },
    /**
     * POST /pms/service-requests/{id}/route — link a task (link_type='target',
     * i.e. "this intent was dispatched to that task") + status →
     * resolved_with_task. Idempotent. 这是 intent → task 唯一的 convert 回链;
     * link_type='source' 是反方向(intent 是在某个 task 里报出来的),不走这里。
     */
    route: async (id: string, data: { taskId: string }) => {
        const r = await axios.post(`/pms/service-requests/${id}/route`, data);
        // 两边都脏了:intent 落终态,被挂上的 task 多了一条 target 链接。
        emitEntityChanged({ entity: "intent", id });
        emitEntityChanged({ entity: "task", id: data.taskId ? String(data.taskId) : null });
        return r;
    },
};

// ListingsOwner API
export const listingsOwnerApi = {
    addListingOwner: (id: string, params: any) =>
        axios.post(`/pms/listings/${id}/owner`, params),
    updateListingOwner: (id: string, ownerId: string, params: any) =>
        axios.put(`/pms/listings/${id}/owner/${ownerId}`, params),
    deleteListingOwner: (id: string, ownerId: string) =>
        axios.delete(`/pms/listings/${id}/owner/${ownerId}`),
};

// Listing OTA Info API
export const listingOtaInfoApi = {
    searchOtaInfo: (params: any) =>
        axios.post<any, any>("/pms/listing-ota-info/search", params),
    getOtaInfo: (id: string) =>
        axios.get<any, any>(`/pms/listing-ota-info/${id}`),
    updateOtaInfo: (id: string, data: any) =>
        axios.put<any, any>(`/pms/listing-ota-info/${id}`, data),
};

// Reservations API
export const reservationsApi = {
    getReservation: (id: string) =>
        axios.get<any, Reservation>(
            `/pms/reservations/${id}?load_relationships=listing&load_relationships=guest`,
        ),
    searchReservation: (data: any) =>
        axios.post("/pms/reservations/search", data),
    getRelatedTasks: (id: string) =>
        axios.get(`/pms/reservations/${id}/related-tasks`),
    /** 后台读住宿守则同意证据（append-only 全量历史，最新在前）。
     *  guest-facing 的 /guestportal/onboarding-state 是匿名端点且不含 rulesLocale，后台不能用。 */
    getHouseRulesAgreement: (id: SnowflakeId) =>
        axios.get<any, HouseRulesAgreementHistory>(
            `/pms/reservations/${id}/house-rules-agreement`,
        ),
    // Partial update (PATCH, exclude_unset on the backend) — used for custom
    // fields (extraInfo) without resending the full reservation.
    updateReservation: (id: string, data: any) =>
        axios.patch(`/pms/reservations/${id}`, data),
    batchDeleteReservation: (data: any) =>
        axios.post("/pms/reservations/batch-delete", data),
    // NOTE: requires DELETE /pms/reservations/{id} on main-api if not already present
    deleteReservation: (id: string) =>
        axios.delete(`/pms/reservations/${id}`),
    createReservation: (data: CreateReservationInput) =>
        axios.post<any, Reservation>("/pms/reservations", data),
    // 门禁登记状态。当前只为拿 guest_registration_link：属于"客人自行登记"的社区
    // （如 Windsor Cay，每套房子一条独立链接）不由我们代登记，界面要把"重新登记"
    // 按钮换成"复制登记链接"。字段是 snake_case——这批端点不过 camelCase 转换器。
    getGateAccessStatus: (id: string) =>
        axios.get<any, GateAccessStatusResponse>(
            `/pms/reservations/${id}/gate-access/status`,
        ),
    // Guest Form 登记表。404 = 该预订没有登记表行（正常业务态，区块整体隐藏），
    // 用 suppressNotFoundToast 声明为探测型 GET，否则每次打开无表预订的详情页
    // 都会弹一条全局 "Resource not found"。
    // schemaSnapshot / formData 内层键是 snake_case 原样 JSONB（不过 camel
    // 转换器），见 @/types/guest-form 的注释。
    getGuestForm: (id: string) =>
        axios.get<any, GuestFormDetail>(
            `/pms/reservations/${id}/guest-form`,
            { suppressNotFoundToast: true } as any,
        ),
    // PUT body 顶层 camel 包裹 { formData }，内层 snake key 原样传；可写
    // guest+internal 段；无行但模板匹配时后端会创建行。校验失败 422（axios
    // 拦截器翻成 typed Error，消息在 err.message）。
    updateGuestForm: (id: string, formData: Record<string, unknown>) =>
        axios.put<any, GuestFormDetail>(
            `/pms/reservations/${id}/guest-form`,
            { formData },
        ),
};

// Upsell Requests API
export const upsellRequestsApi = {
    searchUpsellRequest: (data: any) =>
        axios.post("/pms/upsell-requests/search", data),
    getUpsellRequest: (id: string) =>
        axios.get(`/pms/upsell-requests/${id}`),
    // 幂等：已读行重复调用返回首次的 readAt。读态是全租户共享的。
    markUpsellRequestRead: (id: string) =>
        axios.post(`/pms/upsell-requests/${id}/read`),
    createUpsellRequest: (data: any) =>
        axios.post("/pms/upsell-requests", data),
    updateUpsellRequest: (id: string, data: any) =>
        axios.put(`/pms/upsell-requests/${id}`, data),
    deleteUpsellRequest: (id: string) =>
        axios.delete(`/pms/upsell-requests/${id}`),
    batchReconcileSettle: (data: any) =>
        axios.post("/pms/upsell-requests/batch-reconcile-settle", data),
    batchUpdateUpsellRequests: (data: any) =>
        axios.post("/pms/upsell-requests/batch-update", data),

    // ---- Refunds (finance.transaction 子资源) ----
    // 退款不是 upsell_request 上的一个状态，而是账本里的独立交易行。
    // 注意返回值：axios 响应拦截器已经把 response.data 解包出来了，后端这两个端点
    // 返回的是**裸数组 / 裸对象**，调用方拿到的就是它 —— 不要再 `.data` 一层。
    /** GET /pms/upsell-requests/{id}/refunds — 退款交易列表（金额为负），权限 pms:upsell_request:read */
    listRefunds: (id: string) =>
        axios.get(`/pms/upsell-requests/${id}/refunds`),
    /** POST /pms/upsell-requests/{id}/refunds — 登记一笔退款，权限 pms:upsell_request:refund */
    createRefund: (id: string, data: {
        amount: number;
        refundReasonCode: string;
        refundDate: string;
        notes?: string;
        executeInStripe?: boolean;
    }) => axios.post(`/pms/upsell-requests/${id}/refunds`, data),
};

// Finance API —— 通用财务账本（finance.transaction）与银行对账组。
// 交易金额带符号（进账为正、出账为负），且序列化成 **string**：算术前先 Number()。
export const financeApi = {
    searchTransactions: (data: any) => axios.post("/finance/transactions/search", data),
    getTransaction: (id: string) => axios.get(`/finance/transactions/${id}`),
    createTransaction: (data: any) => axios.post("/finance/transactions", data),
    /**
     * PUT /finance/transactions/{id} —— 部分更新（只发要改的键）。
     *
     * 后端 `TransactionUpdateRequest` 只收：`amount` / `transactionDate` /
     * `refundReasonCode` / `notes` / `sourceType` + `sourceId`。**故意不收**
     * `transactionType` / `provider` / `groupId`（改类型等于换一笔账；入组走对账组端点），
     * 且 `extra='forbid'` —— 多传一个键就是 422。
     *
     * `sourceType` + `sourceId` 是**未归属退款的手工指派**入口：写进去之后后端会
     * 重算该 upsell 订单行的 `totalRefundedAmount` / `netAmount`（生成列自动跟随），
     * 并且**旧归属方也一起重算**。两列同空同有（库里有 CHECK 约束）。
     *
     * 交易所在的组已对账时一律 403（`TRANSACTION_IN_RECONCILED_GROUP`）。
     */
    updateTransaction: (id: string, data: any) => axios.put(`/finance/transactions/${id}`, data),
    deleteTransaction: (id: string) => axios.delete(`/finance/transactions/${id}`),
    /**
     * GET /finance/transactions/{id}/assignment-candidates
     *
     * 未归属退款能指派给哪几行 upsell 订单 —— 候选 = **同一个 paymentIntentId 下的
     * 订单行**（与 webhook 判定归属时同一个查询口径），不是全库 upsell 搜索。
     * 空数组有两种正常含义：该交易没有 paymentIntentId，或该 payment_intent
     * 在本系统里没有对应订单行。
     *
     * 权限是 `finance:transaction:update`（不是 read）—— 候选集唯一的用途就是
     * 紧接着那次指派写入。
     */
    listAssignmentCandidates: (id: string) =>
        axios.get(`/finance/transactions/${id}/assignment-candidates`),

    searchGroups: (data: any) => axios.post("/finance/reconciliation-groups/search", data),
    getGroup: (id: string) => axios.get(`/finance/reconciliation-groups/${id}`),
    createGroup: (data: any) => axios.post("/finance/reconciliation-groups", data),
    updateGroup: (id: string, data: any) => axios.put(`/finance/reconciliation-groups/${id}`, data),
    /**
     * DELETE /finance/reconciliation-groups/{id}
     *
     * 后端拒绝删除**已对账**的组（403，先 Unreconcile）；删除时组内交易的
     * `group_id` 被置空，交易本身保留（账本不能因为删了一张对账单就少一笔钱）。
     */
    deleteGroup: (id: string) => axios.delete(`/finance/reconciliation-groups/${id}`),
    addToGroup: (id: string, data: any) =>
        axios.post(`/finance/reconciliation-groups/${id}/transactions`, data),
    removeFromGroup: (id: string, txId: string) =>
        axios.delete(`/finance/reconciliation-groups/${id}/transactions/${txId}`),
    reconcileGroup: (id: string) => axios.post(`/finance/reconciliation-groups/${id}/reconcile`),
    unreconcileGroup: (id: string) => axios.post(`/finance/reconciliation-groups/${id}/unreconcile`),
};

// SQLAlchemy relationship attribute names on PurchaseModel (see
// main-api/.../purchase_model.py). Names are snake_case at the ORM level even
// though the JSON response is camelCase — load_relationships matches the
// attribute, not the serialized field.
const DEFAULT_PURCHASE_RELATIONSHIPS = [
    'vendor',
    'ship_to_listing',
    'product',
    'orderer',
    'receiver',
    'reconciler',
    'creator',
    'updater',
];

// Procurement Purchases API (line-level model)
export const procurementApi = {
    /** POST /procurement/purchases/search — Filter DSL search */
    search: (data: any) =>
        axios.post("/procurement/purchases/search", data),

    /**
     * GET /procurement/purchases/{id}?load_relationships=...
     *
     * Backend uses SQLAlchemy relationship names (snake_case attribute names
     * on PurchaseModel) — NOT a `columns` projection. Default loads every
     * join the detail flyout / route page renders (vendor, ship-to, product,
     * actor users). Pass an explicit list to narrow.
     */
    getById: (id: string, loadRelationships: string[] = DEFAULT_PURCHASE_RELATIONSHIPS) => {
        const qs = loadRelationships.length
            ? '?' + loadRelationships.map(r => `load_relationships=${encodeURIComponent(r)}`).join('&')
            : '';
        return axios.get(`/procurement/purchases/${id}${qs}`) as Promise<Purchase>;
    },

    /** POST /procurement/purchases — single create */
    create: (data: PurchaseCreateItem) =>
        (axios.post("/procurement/purchases", data) as Promise<Purchase>).finally(() => invalidateProductPackInfo()),

    /**
     * POST /procurement/purchases/plan —「去店里买」（方案 2026-11-03）：选几条申请，每个产品建一行
     * `ordered`、价钱空的采购单并挂上申请；createTask 时给买的人建任务挂上它们。任务种类后端给（D18）。
     * 权限 procurement:purchase:create；createTask 另要 pms:task:create。
     */
    plan: (body: {
        materialRequestIds: string[];
        vendorId: string;
        orderedBy?: string;
        estimatedDeliveryDate: string;
        shipToListingId?: string;
        createTask?: boolean;
        notes?: string;
    }) =>
        // 404（申请 / 供应商不在了）由面板自己说：拦截器再弹一次就是两条 toast
        (axios.post("/procurement/purchases/plan", body, { suppressNotFoundToast: true } as any) as Promise<{ purchases: Purchase[]; taskId?: SnowflakeId | null }>)
            .finally(() => invalidateProductPackInfo()),

    /** POST /procurement/purchases/batch_create — same-vendor multi-line create */
    // 建 / 改采购单会改规格的最近单价、可能新建规格：清采购规格缓存
    batchCreate: (items: PurchaseCreateItem[]) =>
        (axios.post("/procurement/purchases/batch_create", {
            // 本页会处理候选（batchCreateWithChoices）：告诉服务端可以问；不带的旧客户端照旧建
            items: items.map(item => ({ ...item, askProductChoice: true })),
        }) as Promise<PurchaseBatchCreateResponse>).finally(() => invalidateProductPackInfo()),

    /** PUT /procurement/purchases/{id} — partial update */
    update: (id: string, data: PurchaseUpdatePayload) =>
        (axios.put(`/procurement/purchases/${id}`, data) as Promise<Purchase>).finally(() => invalidateProductPackInfo()),

    /** DELETE /procurement/purchases/{id} */
    delete: (id: string) =>
        axios.delete(`/procurement/purchases/${id}`),

    /** POST /procurement/purchases/actions — unified receive/return/cancel/reconcile */
    actions: (action: PurchaseActionType, items: any[]) =>
        axios.post("/procurement/purchases/actions", {
            action,
            items,
        }) as Promise<PurchaseActionResponse>,

    // Convenience wrappers (delegate to .actions)
    receive:   (items: PurchaseReceiveItem[])   =>
        axios.post("/procurement/purchases/actions", { action: "receive",   items }) as Promise<PurchaseActionResponse>,
    return:    (items: PurchaseReturnItem[])    =>
        axios.post("/procurement/purchases/actions", { action: "return",    items }) as Promise<PurchaseActionResponse>,
    cancel:    (items: PurchaseCancelItem[])    =>
        axios.post("/procurement/purchases/actions", { action: "cancel",    items }) as Promise<PurchaseActionResponse>,
    reconcile: (items: PurchaseReconcileItem[]) =>
        axios.post("/procurement/purchases/actions", { action: "reconcile", items }) as Promise<PurchaseActionResponse>,
    /** correct_receipt — supervisor down-adjusts an over-received qty; backend reverses inventory by the delta */
    correctReceipt: (items: PurchaseCorrectReceiptItem[]) =>
        axios.post("/procurement/purchases/actions", { action: "correct_receipt", items }) as Promise<PurchaseActionResponse>,
};

// Vendors API
export const vendorApi = {
    search: (data: any) => axios.post("/procurement/vendors/search", data),
    getById: (id: string) => axios.get(`/procurement/vendors/${id}`),
    create: (data: any) => axios.post("/procurement/vendors", data),
    update: (id: string, data: any) => axios.put(`/procurement/vendors/${id}`, data),
    delete: (id: string) => axios.delete(`/procurement/vendors/${id}`),
};

// Material Requests API
/**
 * @deprecated Use `taskExpenseApi` instead. Kept temporarily for `procurement.tsx`
 *   until the procurement module migrates to task_expense. Backend `material_request`
 *   table is scheduled for removal.
 */
export const materialRequestApi = {
    search: (data: any, config?: any) => axios.post("/pms/material-requests/search", data, config),
    getById: (id: string) => axios.get(`/pms/material-requests/${id}`),
    create: (data: any) => axios.post("/pms/material-requests", data),
    batchCreate: (data: any) => axios.post("/pms/material-requests/batch", data),
    update: (id: string, data: any) => axios.put(`/pms/material-requests/${id}`, data),
    delete: (id: string) => axios.delete(`/pms/material-requests/${id}`),
    close: (id: string) => axios.post(`/pms/material-requests/${id}/close`),
    // Stage operations
    getStages: (id: string) => axios.get(`/pms/material-requests/${id}/stages`),
    createStage: (id: string, data: any) => axios.post(`/pms/material-requests/${id}/stages`, data),
    updateStage: (id: string, stageId: string, data: any) => axios.put(`/pms/material-requests/${id}/stages/${stageId}`, data),
    // Bulk operations
    generatePO: (data: any) => axios.post("/pms/material-requests/generate-po", data),
    linkDeliveryTask: (data: { requestIds: string[]; taskId: string }) => axios.post("/pms/material-requests/link-delivery-task", data),
    fulfillToSource: (data: any) => axios.post("/pms/material-requests/fulfill-to-source", data),
    // Individual operations
    selectProduct: (id: string, data: { entity_type: string; entity_id: string; quantity?: number }) => axios.post(`/pms/material-requests/${id}/select-product`, data),
    requestOwnerApproval: (id: string, data: any) => axios.post(`/pms/material-requests/${id}/request-owner-approval`, data),
    linkApprovalTask: (id: string, data: { taskId: string }) => axios.post(`/pms/material-requests/${id}/link-approval-task`, data),
    confirmDelivery: (id: string, data: any) => axios.post(`/pms/material-requests/${id}/confirm-delivery`, data),
    // Task association
    getByTask: (taskId: string) => axios.get(`/pms/material-requests/by-task/${taskId}`),
};

// The actions endpoint does NOT run the global camel→snake body transform, so any
// snake-keyed field must be mapped explicitly. `set_pickup_source` carries `listingId`
// → send as `listing_id`. (Other action fields — sourcingChannel /
// procurementPurchaseId — match the backend schema's to_camel aliases and are
// accepted as-is.)
type SendableTaskExpenseAction = TaskExpenseAction & { virtualListingId?: SnowflakeId };

function toTaskExpenseActionItem(id: SnowflakeId, action: SendableTaskExpenseAction): Record<string, unknown> {
  const {
    listingId, virtualListingId, onBehalfOfUserId, overrideInsufficientStock, overrideReason, ...rest
  } = action as SendableTaskExpenseAction & {
    listingId?: SnowflakeId;
    onBehalfOfUserId?: SnowflakeId;
    overrideInsufficientStock?: boolean;
    overrideReason?: string;
  };
  const item: Record<string, unknown> = { id, ...rest };
  // `listing_id` 是取货来源；`virtual_listing_id` 是货进哪个虚拟地点，两回事
  if (listingId !== undefined) item.listing_id = listingId;
  if (virtualListingId !== undefined) item.virtual_listing_id = virtualListingId;
  // 代记取货(B4)。漏了这个映射的话后端读不到,货会进**点按钮那个人**的车而不是
  // 收货人的 —— 两个人的账同时错,而且界面上一切正常。
  if (onBehalfOfUserId !== undefined) item.on_behalf_of_user_id = onBehalfOfUserId;
  // 收口时 choose_source my_stock 的余额覆盖（方案 2026-11-03 B6）
  if (overrideInsufficientStock !== undefined) item.override_insufficient_stock = overrideInsufficientStock;
  if (overrideReason !== undefined) item.override_reason = overrideReason;
  return item;
}

type TaskExpenseActionRow = {
  id: SnowflakeId;
  ok: boolean;
  data?: TaskExpense;
  // `details` 与单笔路径同一份东西:业务异常的 data 原样搬过来,不过 schema,
  // 一律 snake_case。批量备料撞上 INSUFFICIENT_STOCK 时,「需 5 / 现有 2」
  // 就在这里——类型上不声明的话调用方读不到,后端补的字段等于没补。
  error?: { code: string; message: string; details?: Record<string, unknown> };
};

function postTaskExpenseActions(items: Array<{ id: SnowflakeId } & SendableTaskExpenseAction>) {
  return axios.post(`/pms/task-expenses/actions`, {
    items: items.map(({ id, ...action }) => toTaskExpenseActionItem(id, action as SendableTaskExpenseAction)),
  }) as unknown as Promise<{ items: TaskExpenseActionRow[] }>;
}

/**
 * 批量里被 VIRTUAL_LOCATION_REQUIRED 挡住的行：问一次，重发能用所选地点的行，结果合回 `resp`。
 * 代记的行可能属于不同的人，所以只重发 `details.listing_ids` 含所选 id 的行，其余保留原错误。
 * 这一步失败时保留第一轮的结果：那一轮成功的行已经落库，不能当整批失败。
 */
async function retryWithVirtualPlace(
  items: Array<{ id: SnowflakeId } & TaskExpenseAction>,
  resp: { items: TaskExpenseActionRow[] },
) {
  const blocked = (resp?.items ?? []).filter(
    (row) => !row.ok && row.error?.code === 'VIRTUAL_LOCATION_REQUIRED',
  );
  if (blocked.length === 0) return;
  const choice = await askVirtualPlace(virtualPlaceChoices(blocked[0].error?.details));
  if (!choice) return;
  const retryIds = new Set(
    blocked
      .filter((row) => {
        const ids = virtualPlaceChoices(row.error?.details);
        return !ids || ids.includes(String(choice));
      })
      .map((row) => String(row.id)),
  );
  try {
    const retried = await postTaskExpenseActions(
      items
        .filter((item) => retryIds.has(String(item.id)))
        .map((item) => ({ ...item, virtualListingId: choice })),
    );
    const byId = new Map((retried?.items ?? []).map((row) => [String(row.id), row]));
    resp.items = resp.items.map((row) => byId.get(String(row.id)) ?? row);
  } catch {
    // 被挡的行保留原错误，入口按部分失败提示
  }
}

async function sendTaskExpenseAction(id: SnowflakeId, action: SendableTaskExpenseAction) {
  const resp = await postTaskExpenseActions([{ id, ...action }]);
  const result = resp.items?.[0];
  if (!result) {
    throw new Error('Empty batch action response');
  }
  if (!result.ok) {
    const err = new Error(result.error?.message ?? 'Action failed') as Error & {
      code?: string;
      details?: Record<string, unknown>;
    };
    err.code = result.error?.code;
    // 🔴 `details` 必须一起带出来。这个批量端点是 task-expense 动作的**唯一**
    // 入口(单笔调用方也发 `items:[单条]`),逐行失败不抛 HTTP 错误——结果在
    // `items[].ok` 里,由这里翻译成一个 Error。所以这里丢掉什么,前端就永远拿
    // 不到什么:后端 2026-08-31 专门把业务异常的 data 搬进 per-row ActionError,
    // 就是为了让 INSUFFICIENT_STOCK 的「需 5 / 现有 2」能到界面上。
    //
    // 原样透传、不 camel 化:它是**不过 schema** 的原始 dict(与顶层 HTTP 错误体
    // 的 details 同一份东西、同一个口径),一律 snake_case,Snowflake 已在后端构造
    // 处转成字符串。转了反而跟 `parseStockShortage` 对不上。
    //
    // 没有就不设、不造空对象:`undefined` 和 `{}` 对解析层是两回事,后者会让
    // 「有明细但字段不全」和「根本没有明细」看起来一样。
    if (result.error?.details !== undefined) err.details = result.error.details;
    throw err;
  }
  const data = result.data as TaskExpense;
  emitEntityChanged({ entity: "task", id: data?.taskId ? String(data.taskId) : null });
  return data;
}

// Task Expense API
export const taskExpenseApi = {
  // NOTE: GET /pms/task-expenses is intentionally NOT exposed here. Backend
  // uses Pydantic v2 + alias_generator=to_camel, so FastAPI registers query
  // params under the camelCase alias only (taskId / lineType etc.). Sending
  // snake_case query names silently leaves filters unset and the endpoint
  // returns ALL rows — leaking other tasks' expenses into per-task views.
  //
  // Use `search` (POST /pms/task-expenses/search) below for ALL list/filter
  // operations. DSL filter keys are camelCase (matching Pydantic alias) and
  // type='string' preserves Snowflake ID precision. This mirrors main-mobile
  // supplyService.listByTask.

  /**
   * POST /pms/task-expenses/search
   * DSL filter body. Filter keys are camelCase (e.g. `lineType`, NOT `line_type`).
   * `columns` accepts flat task_expense fields AND nested relation paths —
   * `product.*` / `procurementPurchase.*` (incl. `procurementPurchase.vendor.*`)
   * are all in production use (see `EXPENSE_LINE_COLUMNS` / `FLYOUT_COLUMNS`).
   * ⚠️ 点名嵌套字段时必须连它的 `id` 一起点(后端只给被点名的关系路径补 id),
   * 漏了会在响应校验阶段 500。
   */
  search: async (payload: any) => axios.post(`/pms/task-expenses/search`, payload),

  /**
   * GET /pms/task-expenses/material-frequency?name=<str>
   * Counts same-name free-text material requests. camelCase auto-unwrapped.
   */
  /**
   * POST /pms/task-expenses/release-auto-approved（2026-10-22）
   * 产品 / 分类刚改成自动通过：dryRun=true 数一数还在等审批、按现在设置不用审批的申请；false 放行它们（落 ready）。
   */
  releaseAutoApproved: async (body: { productIds?: string[]; categoryId?: string; dryRun: boolean }) =>
    axios.post(`/pms/task-expenses/release-auto-approved`, body) as Promise<{ count: number; ids: string[] }>,


  /**
   * GET /pms/task-expenses/{id}/return-summary
   * Scene-8 退料/纠错 numbers for a consumed materials line. All ints.
   * `remaining` is the max the manager may return. camelCase auto-unwrapped.
   */
  returnSummary: async (id: SnowflakeId) =>
    axios.get(`/pms/task-expenses/${id}/return-summary`) as Promise<{
      consumed: number;
      returned: number;
      remaining: number;
    }>,

  /**
   * GET /pms/task-expenses/{id}
   * Phase 1: no relationship preload. Caller looks up related entities by ID
   * via separate endpoints (lookups for users, tasks, etc.).
   */
  getById: async (id: SnowflakeId) =>
    axios.get(`/pms/task-expenses/${id}`) as Promise<TaskExpense>,

  /**
   * 「我的库存」（selfSupplied）的人有多个虚拟地点时后端回 VIRTUAL_LOCATION_REQUIRED：问一次
   * 用哪个（`opts.askVirtual`，一次提交多行时传 `sharedVirtualAsk()` 共用一问），带上
   * `virtual_listing_id` 重发；取消就按原来的错误抛。
   */
  create: async (
    payload: TaskExpenseCreatePayload,
    opts: { askVirtual?: (listingIds?: string[]) => Promise<SnowflakeId | null> } = {},
  ) => {
    // NOTE: keys are whitelisted/mapped explicitly here (not a generic
    // camel→snake pass), so any NEW field must be added below or it is
    // silently dropped.
    const snake = {
      task_id: payload.taskId,
      line_type: payload.lineType,
      product_id: payload.productId,
      name: payload.name,
      quantity: payload.quantity,
      expense_category_code: payload.expenseCategoryCode,
      notes: payload.notes,
      amount: payload.amount,
      currency_code: payload.currencyCode,
      bill_to_code: payload.billToCode,
      pmc_charge_share_percent: payload.pmcChargeSharePercent,
      owner_charge_share_percent: payload.ownerChargeSharePercent,
      vendor_name: payload.vendorName,
      recognition_date: payload.recognitionDate,
      description_on_owner_statement: payload.descriptionOnOwnerStatement,
      // 怎么拿（方案 2026-11-03 §4.1）：都不带 = 待定，由执行人选。
      // 以前 sourceListingId 只写在类型里、这里没映射，Web 加的料一律不带来源。
      source_listing_id: payload.sourceListingId ?? undefined,
      self_supplied: payload.selfSupplied || undefined,
      sourcing_channel: payload.sourcingChannel,
      links: payload.links?.length ? payload.links : undefined,
      virtual_listing_id: payload.virtualListingId,
    };
    let created: TaskExpense;
    try {
      created = await (axios.post(`/pms/task-expenses`, snake) as Promise<TaskExpense>);
    } catch (err) {
      if (!payload.selfSupplied || !isVirtualLocationRequired(err)) throw err;
      const choice = await (opts.askVirtual ?? askVirtualPlace)(
        virtualPlaceChoices((err as { details?: unknown }).details),
      );
      if (!choice) throw err;
      created = await (axios.post(`/pms/task-expenses`, { ...snake, virtual_listing_id: choice }) as Promise<TaskExpense>);
    }
    emitEntityChanged({ entity: "task", id: payload.taskId ? String(payload.taskId) : null });
    return created;
  },

  /**
   * PUT /pms/task-expenses/{id}  (partial update; backend has no PATCH).
   *
   * Whitelisted fields only — backend `TaskExpenseUpdateRequest` is
   * `extra='forbid'`. Sending any of these returns 422:
   *   status / line_type / quantity / product_id / sourcing_channel /
   *   procurement_purchase_id / procurement_order_id / task_id / tenant_id.
   *
   * Use `executeAction(id, { action: 'link_to_purchase', procurementPurchaseId })`
   * to attach a purchase. Use `executeAction({ action: 'update_qty' })` for
   * qty mutations. State transitions also go through POST /actions.
   */
  patch: async (id: SnowflakeId, payload: TaskExpensePatchPayload) => {
    const snake: Record<string, any> = {};
    if (payload.name !== undefined) snake.name = payload.name;
    if (payload.expenseCategoryCode !== undefined) snake.expense_category_code = payload.expenseCategoryCode;
    if (payload.notes !== undefined) snake.notes = payload.notes;
    if (payload.amount !== undefined) snake.amount = payload.amount;
    if (payload.currencyCode !== undefined) snake.currency_code = payload.currencyCode;
    if (payload.billToCode !== undefined) snake.bill_to_code = payload.billToCode;
    if (payload.pmcChargeSharePercent !== undefined) snake.pmc_charge_share_percent = payload.pmcChargeSharePercent;
    if (payload.ownerChargeSharePercent !== undefined) snake.owner_charge_share_percent = payload.ownerChargeSharePercent;
    if (payload.vendorName !== undefined) snake.vendor_name = payload.vendorName;
    if (payload.recognitionDate !== undefined) snake.recognition_date = payload.recognitionDate;
    if (payload.descriptionOnOwnerStatement !== undefined) snake.description_on_owner_statement = payload.descriptionOnOwnerStatement;
    // 链接（2026-10-29）：项内字段 url / vendor / note 本来就是蛇形同名
    if (payload.links !== undefined) snake.links = payload.links;
    const updated = await (axios.put(`/pms/task-expenses/${id}`, snake) as Promise<TaskExpense>);
    // 返回体带 taskId → 精确广播;万一缺失退化成通配。
    emitEntityChanged({ entity: "task", id: updated?.taskId ? String(updated.taskId) : null });
    return updated;
  },

  /**
   * 多个虚拟地点时后端回 VIRTUAL_LOCATION_REQUIRED：问一次用哪个，带上 `virtualListingId` 重发。
   * 取消就按原来的错误抛。见 features/task-expense/virtualPlaceChoice.ts。
   */
  executeAction: async (id: SnowflakeId, action: TaskExpenseAction) => {
    try {
      return await sendTaskExpenseAction(id, action);
    } catch (err) {
      if (!isVirtualLocationRequired(err)) throw err;
      const choice = await askVirtualPlace(virtualPlaceChoices((err as { details?: unknown }).details));
      if (!choice) throw err;
      return sendTaskExpenseAction(id, { ...action, virtualListingId: choice });
    }
  },

  executeActionsBatch: async (
    items: Array<{ id: SnowflakeId } & TaskExpenseAction>,
  ) => {
    const resp = await postTaskExpenseActions(items);
    await retryWithVirtualPlace(items, resp);
    // 逐行广播成功的那些(一个合并窗内会被并成一次投递);全失败则不广播。
    for (const row of resp?.items ?? []) {
      if (row?.ok) {
        emitEntityChanged({ entity: "task", id: row.data?.taskId ? String(row.data.taskId) : null });
      }
    }
    return resp;
  },

  // NOTE: backend does NOT provide DELETE — soft-delete is modelled as the
  // `cancel` action (transitions to status="cancelled"). Use executeAction
  // with { action: 'cancel', text: '<reason>' }.

  // NOTE: close-batch endpoint is Phase 2; not implemented in Phase 1 backend.
  // Kept here as a placeholder; do not call until backend ships it.
  closeBatch: async (_payload: { lineIds: SnowflakeId[]; statementRef: string }) => {
    throw new Error('taskExpenseApi.closeBatch is Phase 2; backend endpoint not yet available');
  },
};

// Owners API
export const ownersApi = {
    // 基础 CRUD
    getOwnerList: (params?: any) => axios.get<any>(`/pms/owners`, { params }),
    /** 下拉 / 选择器用：不分页数组 `[{ id, ...nameFields }]`（`pms:owner:read`） */
    lookupOwners: (params: { nameFields: string; orderBy?: string; includeInactive?: boolean; maxResults?: number; filters?: Record<string, any> }) =>
        axios.post<any>(`/pms/owners/lookup`, params),
    searchOwner: (data: any) => axios.post("/pms/owners/search", data),
    getOwner: (id: string) => axios.get<any, Owner>(`/pms/owners/${id}`),
    addOwner: (data: any) => axios.post<any, Owner>(`/pms/owners`, data),
    updateOwner: (id: string, data: any) =>
        axios.put<any, Owner>(`/pms/owners/${id}`, data),
    batchDeleteOwner: (params: { ids: string[] }) =>
        axios.post("/pms/owners/batch-delete", params),

    // Owner-Listing 关联管理
    getOwnerListings: (ownerId: string) =>
        // 后端 controller 返回 {"data": [...]},拦截器解包后是**裸数组**
        // (当前 owner_service 是恒返 [] 的 TODO 占位,实现后消费方即可直接用)
        axios.get<any, OwnerListing[]>(`/pms/owners/${ownerId}/listings`),
    assignListingToOwner: (ownerId: string, data: any) =>
        axios.post(`/pms/owners/${ownerId}/listings`, data),
    removeListingFromOwner: (ownerId: string, listingId: string) =>
        axios.delete(`/pms/owners/${ownerId}/listings/${listingId}`),

    // 业主门户（Nova Owner）账号 —— 契约见 main-api docs/superpowers/specs/2026-10-09-owner-portal-accounts.md
    // 读：pms:owner:read；写：pms:owner:update。没开通时 GET 返回 null（拦截器解包信封后就是 null）。
    // ⚠️ 没开通时后端回的是**裸 `null` 响应体**（不带 {code,data} 信封）。拦截器对空 body 走
    // `return response?.data || response`，`null || response` 会把整个 axios response 对象
    // 当结果交出来——不归一的话界面会把它当成「已开通」。这里按形状判，不是账号一律 null。
    getPortalAccount: (ownerId: string) =>
        axios
            .get<any, unknown>(`/pms/owners/${ownerId}/portal-account`)
            .then((res): OwnerPortalAccount | null => {
                const acct = res as Partial<OwnerPortalAccount> | null | undefined;
                return acct && typeof acct === "object" && typeof acct.username === "string" && acct.userId != null
                    ? (acct as OwnerPortalAccount)
                    : null;
            }),
    // 用户名冲突 / 业主已有账号 → 409
    createPortalAccount: (ownerId: string, data: OwnerPortalAccountCreate) =>
        axios.post<any, OwnerPortalAccount>(`/pms/owners/${ownerId}/portal-account`, data),
    // 管理员重置密码；后端同时踢掉该账号现有会话
    resetPortalAccountPassword: (ownerId: string, data: { password: string }) =>
        axios.put<any, unknown>(`/pms/owners/${ownerId}/portal-account/password`, data),
    // 停用 / 启用
    setPortalAccountStatus: (ownerId: string, data: { isActive: boolean }) =>
        axios.put<any, unknown>(`/pms/owners/${ownerId}/portal-account/status`, data),
    // 删除门户账号：只删登录入口（关联 + 账号匿名化停用 + 踢会话），业主本身与业务数据保留，之后可重新开号；没开通 → 404
    deletePortalAccount: (ownerId: string) =>
        axios.delete<any, unknown>(`/pms/owners/${ownerId}/portal-account`),
};

// Taxonomy API
export const taxonomyApi = {
    getLabelTree: (domainType: string, params?: { max_depth?: number; include_inactive?: boolean }) =>
        axios.get(`/ai/classification/domains/${domainType}/labels/tree`, { params }),
    getChildrenByCode: (parentCode: string) =>
        axios.get(`/ai/classification/domains/taxonomy/labels/children`, { params: { parent_code: parentCode } }),
    getLabelById: (labelId: string) =>
        axios.get(`/ai/classification/labels/${labelId}`),
};

// Default `load_relationships` bundle for the task detail view.
//
// Spec §10.1 / FE plan §3 (2026-05-28 task state layering) — `current_resolution`
// 不在默认 bundle 里。FE 必须从 task 顶层 promoted Layer A 列读"当前 outcome"
// (jobResultTypeCode / jobResultNote / status / wfStage / verificationStatusCode
// / outputForm / jobResultRecordedAt / jobResultLabel)。
//
// 仍可显式请求 ['current_resolution'] 做审计追溯,但禁止用它渲染活态。
// 历史 attempt timeline (AttemptsTimeline / ResolutionSection) 走 `resolutions`
// relation,不依赖 current_resolution。
const TASK_DETAIL_RELATIONSHIPS = [
    'listing',
    'checklists',
    'assignee',
    'supervisor',
    'expenses',
    'intents',
    'attachments',
    'work_sessions',
    'resolutions',     // Layer B — attempts history (AttemptsTimeline / ResolutionSection)
    'children',
    'parent_task',
    'assets',          // Linked assets (Assets tab) — AssetLinkRef rows
    'task_kind',       // Task Kind ref (code/name) for the Kind + estimate row
] as const

// Tasks API
export const tasksApi = {
    addTask: (params: any) => axios.post(`/pms/tasks`, params),
    batchCreateTask: (params: any) => axios.post("/pms/tasks/batch-create", params),
    /**
     * Patch a task's user-editable fields.
     *
     * Body MUST conform to {@link TaskUpdateBody} — only the 13 whitelisted,
     * non-state fields are accepted. State changes (status, actual_*, work
     * session, assignment status, etc.) go through `taskActions.*` instead
     * (Plan D spec §6.2.1).
     *
     * Note: server returns 422 when `assigneeId` is patched on a task whose
     * status !== 'open' — use `taskActions.reassign()` in that case.
     */
    updateTask: async (id: string, params: TaskUpdateBody) => {
        const updated = await axios.put<any, Task>(`/pms/tasks/${id}`, params);
        // 脏标记广播:行内改字段 / snooze / unsnooze 之后,看板卡与已打开的
        // flyout 各自重取(见 lib/entityChangeBus)。
        emitEntityChanged({ entity: "task", id });
        return updated;
    },
    /**
     * GET /pms/tasks/{id}?load_relationships=...
     *
     * `relationships` defaults to the full detail-view bundle (spec §10.1 —
     * 不再包含 `current_resolution`;FE plan §3). Pass a smaller list to skip
     * expensive relations on cheaper refreshes (e.g. after a single-field PUT
     * we may only need `['listing']`). Pass `['current_resolution']` only for
     * audit/追溯,不要用它渲染当前 outcome — 读 task 顶层 promoted 列
     * (jobResultTypeCode / jobResultNote / status / etc.) 替代。
     *
     * `options.suppressNotFoundToast`:404 是正常态的调用方(助手回答里的任务卡片——任务可能已删)
     * 自己降级显示,不弹全局 toast(见 axios-config 的同名开关)。
     */
    getTask: (id: string, relationships?: readonly string[], options?: { suppressNotFoundToast?: boolean }) => {
        const rels = relationships ?? TASK_DETAIL_RELATIONSHIPS
        const qs = rels.map(r => `load_relationships=${encodeURIComponent(r)}`).join('&')
        const url = `/pms/tasks/${id}${qs ? `?${qs}` : ''}`
        return options?.suppressNotFoundToast ? axios.get(url, { suppressNotFoundToast: true } as any) : axios.get(url)
    },
    searchTask: (params: any) => axios.post("/pms/tasks/search", params),
    batchDeleteTask: (params: any) =>
        axios.post("/pms/tasks/batch-delete", params),
    batchUpdateTask: (params: any) =>
        axios.patch("/pms/tasks/batch-update", params),
    addCheckList: async (id: string, params: any | any[]) => {
        const r = await axios.post(`/pms/tasks/${id}/checklist`, Array.isArray(params) ? params : [params]);
        emitEntityChanged({ entity: "task", id });
        return r;
    },
    // checklist 的 update/del 端点只带 checklist 行 id,拿不到所属 task id ——
    // 发通配事件(订阅方按「可能是我」处理,宁可多刷一次也不漏刷)。
    updateCheckList: async (id: string, params: any) => {
        const r = await axios.put(`/pms/tasks/checklist/${id}`, params);
        emitEntityChanged({ entity: "task" });
        return r;
    },
    delCheckList: async (id: string) => {
        const r = await axios.delete(`/pms/tasks/checklist/${id}`);
        emitEntityChanged({ entity: "task" });
        return r;
    },
    addExpense: async (id: string, params: any) => {
        const r = await axios.post(`/pms/tasks/${id}/expenses`, params);
        emitEntityChanged({ entity: "task", id });
        return r;
    },
    updateExpense: async (id: string, params: any) => {
        const r = await axios.put(`/pms/tasks/expenses/${id}`, params);
        emitEntityChanged({ entity: "task" });
        return r;
    },
    delExpense: async (id: string) => {
        const r = await axios.delete(`/pms/tasks/expenses/${id}`);
        emitEntityChanged({ entity: "task" });
        return r;
    },
    /**
     * Link one or more assets to a task.
     * POST /pms/tasks/{taskId}/assets  body { assetIds: string[] }
     * Returns the link rows (AssetLinkRef[]).
     */
    linkAssets: (taskId: string, assetIds: string[]) =>
        axios.post<any, AssetLinkRef[]>(`/pms/tasks/${taskId}/assets`, { assetIds }),
    /**
     * Unlink a single asset from a task.
     * DELETE /pms/tasks/{taskId}/assets/{assetId}
     */
    unlinkAsset: (taskId: string, assetId: string) =>
        axios.delete(`/pms/tasks/${taskId}/assets/${assetId}`),
    /**
     * Link an entity to a task.
     * POST /pms/tasks/{taskId}/entity-links
     * Perm: pms:task:update
     */
    createEntityLink: (
        taskId: SnowflakeId,
        body: { entityTypeCode: string; entityId: SnowflakeId; linkType?: string },
    ) =>
        axios.post(`/pms/tasks/${taskId}/entity-links`, {
            entity_type_code: body.entityTypeCode,
            entity_id: body.entityId,
            link_type: body.linkType ?? 'source',
        }),
    /**
     * 任务挂了哪些实体（去店里买：任务挂的采购单，`purchase:fulfillment`）。
     * GET /pms/tasks/{taskId}/entity-links?entityTypeCode=&linkType=
     * Perm: pms:task:read（main-api 12f60a8d3）。先建的在前；读不到任务 404。
     */
    listEntityLinks: (taskId: SnowflakeId, filter: { entityTypeCode: string; linkType?: string }) =>
        axios.get<any, Array<{ id: SnowflakeId; taskId: SnowflakeId; entityTypeCode: string; entityId: SnowflakeId; linkType: string }>>(
            `/pms/tasks/${taskId}/entity-links`,
            { params: { entityTypeCode: filter.entityTypeCode, ...(filter.linkType ? { linkType: filter.linkType } : {}) } },
        ),
    /**
     * 解挂一条任务 → 实体的关联（去店里买：「没买到 → 换一家店」的采购单离开这个任务）。只删关联行，不动实体。
     * DELETE /pms/tasks/{taskId}/entity-links?entityTypeCode=&entityId=&linkType= → 204
     * Perm: pms:task:update（main-api fdfa69afd）。没这条关联 / 读不到任务 404。
     */
    deleteEntityLink: (
        taskId: SnowflakeId,
        link: { entityTypeCode: string; entityId: SnowflakeId; linkType: string },
    ) =>
        axios.delete(`/pms/tasks/${taskId}/entity-links`, {
            params: { entityTypeCode: link.entityTypeCode, entityId: link.entityId, linkType: link.linkType },
        }),
    /**
     * Reverse query — find tasks linked to a given entity.
     * GET /pms/tasks/by-entity?entity_type=review&entity_id={entityId}
     * Returns Task[] (camelCase from backend).
     * Perm: pms:task:read
     */
    getTasksByEntity: ({ entityType, entityId }: { entityType: string; entityId: SnowflakeId }) =>
        axios.get<any, Task[]>('/pms/tasks/by-entity', {
            params: { entity_type: entityType, entity_id: entityId },
        }),
};

// Tags API
export const tagsApi = {
    list:   (params?: any) => axios.get('/pms/tags', { params }),
    search: (params: any)  => axios.post('/pms/tags/search', params),
    get:    (id: string, params?: any) => axios.get(`/pms/tags/${id}`, { params }),
    create: (data: any)    => axios.post('/pms/tags', data),
    update: (id: string, data: any) => axios.put(`/pms/tags/${id}`, data),
    delete: (id: string)   => axios.delete(`/pms/tags/${id}`),
    getTagLookup: (params: any) => axios.post('/pms/tags/lookup', params),
    // Entity-link（给实体挂/摘 tag；后端 pms/tag_controller.py 现成）
    getByEntities: (entityType: string, entityIds: string[]) =>
        axios.get('/pms/tags/by-entities', {
            params: { entity_type: entityType, entity_ids: entityIds },
            // entity_ids 是数组 query 参数：必须序列化成重复的 entity_ids=a&entity_ids=b
            // （FastAPI Query 要的格式），否则 axios 默认 entity_ids[]=a 后端拿不到 → 422。
            // 与 attachmentApi.getAttachment 同款序列化。
            paramsSerializer: (p: any) => {
                const searchParams = new URLSearchParams();
                Object.entries(p).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((v) => searchParams.append(key, String(v)));
                    } else if (value !== undefined && value !== null) {
                        searchParams.append(key, String(value));
                    }
                });
                return searchParams.toString();
            },
        }),
    createEntityLink: (data: { tagId: string; entityType: string; entityId: string }) =>
        axios.post('/pms/tags/entity-link', data),
    deleteEntityLink: (entityId: string, tagId: string) =>
        axios.delete(`/pms/tags/entity-link/${entityId}/${tagId}`),
};

// Comments API
export const commentsApi = {
    getCommentsList: (params: any) => axios.get(`/pms/comments`, { params }),
    addComment: (params: any) => axios.post(`/pms/comments`, params),
};

// Lookup API
export const lookupApi = {
    // includeDisabled:连已停用的字典项一起返回——只给「把历史 code 显示成正式标签」用,下拉选项不要带
    getLookup: (name_field: string, opts?: { includeDisabled?: boolean }) =>
        axios.get(`/system/lookup-table/tables/${name_field}`, opts?.includeDisabled ? { params: { include_disabled: true } } : undefined),
    getAllLookup: (params: any) =>
        axios.get(`/system/lookup-table/all-tables`, { params }),
};

// Catalog API —— 租户级 sys catalog（skill / position / service_line）的下拉数据源。
// 走各自的 POST /lookup（BaseService.lookup，不分页，返回 [{id, code, label, ...}]）。
// 与全局 lookupApi 分开：catalog 是租户级实体，按当前租户隔离。
const CATALOG_LOOKUP_BODY = { nameFields: 'id code label serviceLineCode', orderBy: 'sortOrder' };
export const catalogApi = {
    lookupSkills: () =>
        axios.post(`/system/skills/lookup`, CATALOG_LOOKUP_BODY) as Promise<any[]>,
    lookupPositions: () =>
        axios.post(`/system/positions/lookup`, CATALOG_LOOKUP_BODY) as Promise<any[]>,
    lookupServiceLines: () =>
        axios.post(`/system/service-lines/lookup`, { nameFields: 'id code label', orderBy: 'sortOrder' }) as Promise<any[]>,
};

// Guests API
export const guestsApi = {
    getGuest: (id: string) => axios.get<any, Guest>(`/pms/guests/${id}`),
    searchGuest: (params: any) =>
        axios.post<any, SearchResponse<Guest>>("/pms/guests/search", params),
    batchDeleteGuest: (params: { ids: string[] }) =>
        axios.post("/pms/guests/batch-delete", params),
};

// Groups API
export const groupsApi = {
    getGroupLookup: (params: any) => axios.post(`/pms/groups/lookup`, params),
};

// Views API
//
// View lookup / default 全部按 pageKey 维度。pageKey 是前端定义的 UI page
// 唯一标识符 (如 'tasks' / 'support_cases'), 跟 source (entity type, 如 'task')
// 解耦: 同一 source 可挂多个 pageKey。
export const viewsApi = {
    getViews: (pageKey: string) =>
        axios.post("/system/view/lookup", { pageKey, includeShared: true }),
    createView: (data: any) => axios.post("/system/view", data),
    updateView: (id: string, data: any) =>
        axios.put(`/system/view/${id}`, data),
    deleteView: (id: string) => axios.delete(`/system/view/${id}`),

    // view page
    searchView: (params: any) =>
        // summary 是后端 ViewSummary 形状(totalCount/byAuthority/...),
        // 与前端展示用的 ViewSummary(totalViews/...)不同——消费方自行推导
        axios.post<any, SearchResponse<View> & {
            summary?: { totalCount?: number; byAuthority?: Record<string, number>; bySource?: Record<string, number> }
        }>("/system/view/search", params),
    getView: (id: string) => axios.get(`/system/view/${id}`),

    // Reorder (batch update display_order)
    reorderViews: (items: Array<{ id: string; displayOrder: number }>) =>
        axios.patch("/system/view/reorder", { items }),

    // System-level default view per pageKey (admin-only on backend).
    // Writes sys.view.is_default with exclusive semantics (clear old + set new
    // in single transaction). Requires system:view:update permission.
    setDefaultView: (pageKey: string, viewId: string) =>
        axios.put(`/system/view/default`, { pageKey, viewId }),

    // ViewPanel item count badge.
    // Conditional read (stateToken): pass the token from the previous FULL
    // response; if nothing changed server-side, the reply is
    // { notModified: true, stateToken } with NO counting performed — keep
    // current values. Omit stateToken to force a full recompute (do this
    // after local writes and as a ~5min staleness backstop).
    getCounts: (params: { pageKey: string; viewIds?: string[]; timeoutMs?: number; stateToken?: string }) =>
        axios.post<any, {
            counts: Record<string, number | null>;
            errors: Record<string, string>;
            notModified?: boolean;
            stateToken?: string | null;
        }>(
            "/system/view/counts", params,
        ),
};

// Tenants API
export const tenantsApi = {
    // 基础 CRUD
    getTenantList: (params?: any) =>
        axios.get<any>("/system/tenant", { params }),
    searchTenant: (data: any) => axios.post("/system/tenant/search", data),
    getTenant: (id: string) => axios.get<any, Tenant>(`/system/tenant/${id}`),
    createTenant: (data: any) => axios.post<any, Tenant>("/system/tenant", data),
    updateTenant: (id: string, data: any) =>
        axios.put<any, Tenant>(`/system/tenant/${id}`, data),
    deleteTenant: (id: string) => axios.delete<any>(`/system/tenant/${id}`),

    // 当前用户的租户操作
    // `config` 是 axios 请求配置——探测型调用（无租户的平台账号必 404）传 `{ suppressNotFoundToast: true }`
    getCurrentTenant: (config?: Record<string, unknown>) =>
        axios.get<any>("/system/tenant/current", config),
    updateCurrentTenant: (data: any) =>
        axios.patch<any>("/system/tenant/current", data),

    // API Key 管理 - 当前租户
    getApiKeys: () =>
        axios.get<any>("/system/tenant/api-keys"),
    createApiKey: (data: ApiKeyCreateRequest) =>
        axios.post<any>("/system/tenant/api-keys", data),
    updateApiKey: (keyId: string, data: { name?: string; is_active?: boolean }) =>
        axios.patch<any>(`/system/tenant/api-keys/${keyId}`, data),
    deleteApiKey: (keyId: string) =>
        axios.delete<any>(`/system/tenant/api-keys/${keyId}`),

    // API Key 管理 - 平台管理（指定租户）
    getApiKeysForTenant: (tenantId: string) =>
        axios.get<any>(`/system/tenant/${tenantId}/api-keys`),
    createApiKeyForTenant: (tenantId: string, data: ApiKeyCreateRequest) =>
        axios.post<any, ApiKeyCreateOutput>(`/system/tenant/${tenantId}/api-keys`, data),
    updateApiKeyForTenant: (tenantId: string, keyId: string, data: { name?: string; is_active?: boolean }) =>
        axios.patch<any>(`/system/tenant/${tenantId}/api-keys/${keyId}`, data),
    deleteApiKeyForTenant: (tenantId: string, keyId: string) =>
        axios.delete<any>(`/system/tenant/${tenantId}/api-keys/${keyId}`),

    // API Key Rotate - 当前租户
    rotateApiKey: (keyId: string) =>
        axios.post<ApiKeyCreateOutput>(`/system/tenant/api-keys/${keyId}/rotate`),

    // API Key Rotate - 平台管理（指定租户）
    rotateApiKeyForTenant: (tenantId: string, keyId: string) =>
        axios.post<ApiKeyCreateOutput>(`/system/tenant/${tenantId}/api-keys/${keyId}/rotate`),

    // Access Code（邀请码）管理 - 当前租户，仅 Account Admin 自助，不开放平台管理变体
    // （platform admin 不需要代管某个特定 tenant 的邀请码 —— 见设计讨论：
    //  已有 tenant 加人由 tenant 自己的 Account Admin 全权处理）
    generateAccessCode: (data: AccessCodeGenerateRequest) =>
        axios.post<any, AccessCodeOutput>("/system/tenant/access-codes", data),
    listAccessCodes: () =>
        axios.get<any, AccessCodeOutput[]>("/system/tenant/access-codes"),
    revokeAccessCode: (codeId: string) =>
        axios.delete<any>(`/system/tenant/access-codes/${codeId}`),
};

// User API
export const userApi = {
    getUser: (userId: string) =>
        axios.get(
            // staffinfo 已废弃：不再加载该 relationship（操作字段已在 user 顶层）
            `/system/user/${userId}?load_relationships=roles`,
        ),
    updateUser: (userId: string, params: any) =>
        axios.put(`/system/user/${userId}`, params),
    // 自服务：改自己的资料（姓名/联系方式/头像/settings 等），后端按 auth token 解出
    // 目标用户，不接受也不需要 userId —— 不要在这里传 id，也不要跟 updateUser 合并成一个函数。
    // updateUser 是管理员端点（需要 system:user:update，只有 config.users 角色才有），
    // 普通用户拿它改自己的 profile/settings 会 403（历史 bug，见 my-profile-page.tsx 的调用点）。
    updateCurrentUserProfile: (params: any) =>
        axios.put(`/system/user/current/profile`, params),
    addUser: (params: any) => axios.post("/system/user", params),
    searchUser: (params: any) => axios.post("/system/user/search", params),
    batchDeleteUser: (params: any) =>
        axios.post(`/system/user/batch-delete`, params),
    // 批量设置用户字段（同值）——用于批量停用 { ids, data: { isActive: false } }
    batchSetUser: (params: { ids: string[]; data: Record<string, any> }) =>
        axios.patch(`/system/user/batch-set`, params),
    changePassword: (userId: string, params: any) =>
        axios.post(`/system/user/${userId}/change-password`, params),
    getCurrentUser: () => axios.get(`/system/user/current/info`),
    getUserLookup: (params?: any) => axios.post("/system/user/lookup", params),
    watchEntity: (entity_type: string, entity_id: string) =>
        axios.post(`/pms/user-entity-links/watch/${entity_type}/${entity_id}`),
    getUserEntity: (params: any) =>
        axios.get(`/pms/user-entity-links/my-watches`, params),
    delEntity: (entity_type: string, entity_id: string) =>
        axios.delete(
            `/pms/user-entity-links/unwatch/${entity_type}/${entity_id}`,
        ),
    checkUserField: (params: any) =>
        axios.post(`/system/user/check-exists`, params),
    clearUserCache: (userId: string) =>
        axios.delete(`/system/user/${userId}/cache`),
    getUserDetail: (userId: string) =>
        axios.get(
            // staffinfo 已废弃：从 relationship 列表移除（操作字段已在 user 顶层）
            `/system/user/${userId}?load_relationships=roles&load_relationships=menus&load_relationships=permissions&load_relationships=department&load_relationships=creator&load_relationships=updater`,
        ),
};

export const assetsApi = {
  search: (params: any) => axios.post('/asset-mgr/assets/search', params),
  get: (id: string) => axios.get(`/asset-mgr/assets/${id}`),
  getByCode: (code: string) =>
    axios.get(`/asset-mgr/assets/by-code/${encodeURIComponent(code)}`),
  // ⚠️ Wire param is snake_case `page_size` (plain Query param, no camel alias).
  listEvents: (id: string, params?: { page?: number; pageSize?: number }) =>
    axios.get(`/asset-mgr/assets/${id}/events`, {
      params: { page: params?.page, page_size: params?.pageSize },
    }),
  /**
   * Tasks linked to an asset (Related tasks tab).
   * GET /asset-mgr/assets/{assetId}/tasks → TaskLinkRef[] ({ id, title, status }).
   */
  getTasks: (id: string) =>
    axios.get(`/asset-mgr/assets/${id}/tasks`),
  // B2 unified move — server infers loan/return/redeploy.
  // On any redeploy-without-confirm the backend returns HTTP 400 (MOVE_REQUIRES_PERMANENT_CONFIRM)
  // with details.assetIds; caller re-sends with confirmPermanent:true.
  move: (id: string, body: { toListingId: string; returnBy?: string; confirmPermanent?: boolean; notes?: string }) =>
    axios.post<any, AssetDTO>(`/asset-mgr/assets/${id}/move`, body),
  changeStatus: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/change-status`, body),
  check: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/check`, body),
  addIdentifier: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/identifiers`, body),
  removeIdentifier: (id: string, identifierId: string) =>
    axios.delete(`/asset-mgr/assets/${id}/identifiers/${identifierId}`),
  patch: (id: string, body: any) =>
    axios.patch(`/asset-mgr/assets/${id}`, body),
  setTags: (id: string, tagIds: string[]) =>
    axios.put(`/asset-mgr/assets/${id}/tags`, { tagIds }),
  batchCheck: (body: any) =>
    axios.post('/asset-mgr/assets/checks/batch', body),
  batchMove: (body: { assetIds: string[]; toListingId: string; returnBy?: string; confirmPermanent?: boolean; notes?: string }) =>
    axios.post<any, { results: { assetId: string; resolvedAction: string }[] }>(`/asset-mgr/assets/moves/batch`, body),
  batchStatusChange: (body: any) =>
    axios.post('/asset-mgr/assets/status-changes/batch', body),
  // Directly create serialized assets at a listing (New asset / Import CSV).
  // Backend writes an INBOUND initial_load transaction per unit; does not touch inventory balance.
  directCreate: (body: AssetDirectCreateInput) =>
    axios.post('/asset-mgr/assets/direct-create', body),

  // ── PRD scenario 4/8/9/14 flow actions ──────────────────────────────
  // Verify folded into Check (2026-07-06): use `check` with method:'manual' —
  // the separate /verify entry point is gone.
  // Transfer ownership (who owns it; position stays unless moveToPrimary).
  // 400 on on-loan / terminal-status assets; 404 on unknown target ownership.
  transferOwnership: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/transfer-ownership`, body),
  // Borrowed-out board (returned_at IS NULL) + overdue flag.
  // ⚠️ Wire param AND top-level response keys are snake_case (`page_size`) —
  // the endpoint returns a bare dict, not BaseListResponse (handoff §4.1).
  // items[] entries are normal camelCase LoanRecordOutput objects.
  activeLoans: (params?: { page?: number; pageSize?: number }) =>
    axios.get('/asset-mgr/assets/loans/active', {
      params: { page: params?.page, page_size: params?.pageSize },
    }),
  // Per-asset loan history (paired loan/return events).
  loanHistory: (id: string) =>
    axios.get(`/asset-mgr/assets/${id}/loans`),
  // Report damage (photos + notes + optional task_id) WITHOUT a status change —
  // asset stays in_use; backend writes a `report_damage` event only.
  reportDamage: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/report-damage`, body),
  // Report missing (reason + photos) / recover (pick current location).
  reportMissing: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/report-missing`, body),
  recover: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/recover`, body),
  // Periodic plans inherited by this asset (asset_plan state) + execution history.
  listPlans: (id: string) =>
    axios.get(`/asset-mgr/assets/${id}/plans`),
  // Create a plan that lives on THIS asset only (self-created, not inherited
  // from the product). Body mirrors the product-plan definition shape.
  createOwnPlan: (id: string, body: any) =>
    axios.post(`/asset-mgr/assets/${id}/plans`, body),
  listPlanExecutions: (id: string, params?: { page?: number; pageSize?: number }) =>
    axios.get(`/asset-mgr/assets/${id}/plan-executions`, { params }),

  // ── Guarded delete (2026-08-23) ────────────────────────────────────
  // For "I created this by mistake" only. The backend refuses any asset with
  // history (events beyond its creation deploy, linked tasks, audit rows,
  // executed plans) — Retire is the lifecycle end for those. Always preview
  // first so the confirm dialog can state the consequences up front.
  deletePreview: (assetIds: string[]) =>
    axios.post<any, AssetDeletePreviewResponse>(
      '/asset-mgr/assets/deletions/preview', { assetIds },
    ),
  // 204 on success; 409 CONFLICT_ASSET_NOT_DELETABLE (typed as
  // AssetNotDeletableError by the axios interceptor) when the gate refuses.
  delete: (id: string) => axios.delete(`/asset-mgr/assets/${id}`),
  // Partial success: deletes every clean asset, reports the blocked ones.
  deleteBatch: (assetIds: string[]) =>
    axios.post<any, AssetDeleteBatchResponse>(
      '/asset-mgr/assets/deletions/batch', { assetIds },
    ),
}

// Ownership buckets（归属主体）— who owns an asset and where its home is.
// spec 2026-07-05 asset-ownership-redesign. No DELETE: deactivate via
// PUT {isActive:false}. Create returns 409 for a duplicate
// (tenant, ownerType, primaryListing) bucket.
export const ownershipsApi = {
  // Plain Search DSL (filters/columns/orderBy) — no ownerType sugar here.
  search: (params: any) => axios.post('/asset-mgr/ownerships/search', params),
  // Query-string sugar variant: { ownerType?, primaryListingId?, isActive? }.
  list: (params?: any) => axios.get('/asset-mgr/ownerships', { params }),
  // Detail carries the primaryListing reference object.
  get: (id: string) => axios.get(`/asset-mgr/ownerships/${id}`),
  create: (body: any) => axios.post('/asset-mgr/ownerships', body),
  update: (id: string, body: any) => axios.put(`/asset-mgr/ownerships/${id}`, body),
}

// Periodic-plan definitions on a product + per-asset plan state (PRD scenario 7;
// asset-plan-schedule-design.md). product_plan = definition; asset_plan = per-asset state.
export const assetPlansApi = {
  listForProduct: (productId: string) =>
    axios.get(`/asset-mgr/products/${productId}/plans`),
  createForProduct: (productId: string, body: any) =>
    axios.post(`/asset-mgr/products/${productId}/plans`, body),
  updateDefinition: (planId: string, body: any) =>
    axios.put(`/asset-mgr/product-plans/${planId}`, body),
  removeDefinition: (planId: string) =>
    axios.delete(`/asset-mgr/product-plans/${planId}`),
  // Per-asset state: pause / interval override.
  updateAssetPlan: (assetPlanId: string, body: any) =>
    axios.patch(`/asset-mgr/asset-plans/${assetPlanId}`, body),
}

// 退管：预览归这个地点的在用资产，确认后执行。三种 kind 都能用（DECISIONS S4/D2）
export const assetDecommissionApi = {
  preview: (listingId: string) => axios.get(`/asset-mgr/listings/${listingId}/decommission/preview`),
  execute: (listingId: string) => axios.post(`/asset-mgr/listings/${listingId}/decommission`),
}

// Asset statistics / reports (PRD scenario 5/15/16). Statistics only — to-do lives in tasks.
export const assetReportsApi = {
  dashboard: (params?: any) => axios.get('/asset-mgr/reports/dashboard', { params }),
  repair: (params?: any) => axios.get('/asset-mgr/reports/repair', { params }),
  utilization: (params?: any) => axios.get('/asset-mgr/reports/utilization', { params }),
}

// Body/return for assetsApi.directCreate (POST /asset-mgr/assets/direct-create).
// NOTE: backend serializes the result with snake_case keys; callers should read
// `createdCount ?? created_count` defensively.
export interface AssetDirectCreateItem {
  productId?: string
  sku?: string
  assets: Array<{
    identifiers?: Array<{ codeType: string; codeValue: string }>
    notes?: string | null
    photos?: string[] | null
    /** Loan-out admission flag; omitting falls back to legacy flowType derivation. */
    isLoanable?: boolean
  }>
}
export interface AssetDirectCreateInput {
  listingId: string
  items: AssetDirectCreateItem[]
  sourceType?: string
  referenceNo?: string
}
export interface AssetDirectCreateResult {
  createdAssetIds?: string[]
  createdCount?: number
  created_asset_ids?: string[]
  created_count?: number
}

// Menu API
export const menuApi = {
    // 获取菜单列表
    getMenuList: (params?: any) => axios.get("/system/menu", { params }),
    // 搜索菜单
    searchMenu: (params: any) => axios.post("/system/menu/search", params),
    // 菜单 lookup
    getMenuLookup: (params?: any) => axios.post("/system/menu/lookup", params),
    // 新增
    createMenu: (data: any) => axios.post("/system/menu", data),
    updateMenu: (id: string, data: any) => axios.put(`/system/menu/${id}`, data),
    deleteMenu: (id: string) => axios.delete(`/system/menu/${id}`),
};

// Permission API
export const permissionApi = {
    // 获取权限列表
    getPermissionList: (params?: any) =>
        axios.get("/system/permission", { params }),
    // 搜索权限
    searchPermission: (params: any) =>
        axios.post("/system/permission/search", params),
    // 权限 lookup
    getPermissionLookup: (params?: any) => axios.post("/system/permission/lookup", params),
    // 新增
    createPermission: (data: any) => axios.post("/system/permission", data),
    updatePermission: (id: string, data: any) => axios.put(`/system/permission/${id}`, data),
    deletePermission: (id: string) => axios.delete(`/system/permission/${id}`),
};

// Role API
export const roleApi = {
    searchRole: (params: any) => axios.post("/system/role/search", params),
    getRoleLookup: (params?: any) => axios.post("/system/role/lookup", params),
    // 获取角色详情（含菜单和权限）
    getRoleDetail: (id: string, loadRelationships?: string[]) => {
        const queryParams = new URLSearchParams();
        if (loadRelationships && loadRelationships.length > 0) {
            loadRelationships.forEach((rel: string) => {
                queryParams.append("load_relationships", rel);
            });
        }
        const queryString = queryParams.toString();
        return axios.get<any>(
            `/system/role/${id}${queryString ? `?${queryString}` : ""}`,
        );
    },
    // 更新角色权限
    assignRolePermissions: (roleId: string, permissionIds: string[]) => {
        return axios.post(`/system/role/${roleId}/assign-permissions`, {
            permissionIds: permissionIds,
        });
    },
    // 更新角色菜单
    updateRoleMenus: (
        roleId: string,
        menuIds: string[],
        dataScope: number,
        deptIds?: string[],
    ) => {
        return axios.patch("/system/role/permissions", {
            roleIds: [roleId],
            menuIds: menuIds,
            dataScope: dataScope,
            deptIds: deptIds || [],
        });
    },
    // 创建角色
    createRole: (roleData: any) => {
        return axios.post("/system/role", roleData);
    },
    // 更新角色基本信息
    updateRole: (roleId: string, roleData: any) => {
        return axios.put(`/system/role/${roleId}`, roleData);
    },
    // 删除角色（仅自定义角色）
    deleteRole: (id: string) => axios.delete(`/system/role/${id}`),
    // 获取所有活跃角色的 extraInfo 字段定义（去重），用于新增字段时的下拉建议
    // 通过 lookup 接口获取，前端聚合去重
    getExtraInfoSuggestions: async (excludeRoleId?: string) => {
        const response = await roleApi.getRoleLookup({
            nameFields: "id extraInfo",
            maxResults: 500,
            filters: { isActive: true },
        });
        const roles = Array.isArray(response) ? response : (response as any)?.results || [];
        const fieldMap = new Map<string, any>();
        for (const role of roles) {
            if (excludeRoleId && role.id === excludeRoleId) continue;
            const info = role.extraInfo || role.extra_info;
            if (!Array.isArray(info)) continue;
            for (const field of info) {
                if (!field.key || field.key.startsWith('field_')) continue;
                if (!fieldMap.has(field.key)) fieldMap.set(field.key, { ...field });
            }
        }
        return Array.from(fieldMap.values());
    },
};

// Capability API
export const capabilityApi = {
    // 注册表(全部 group);后端返回 CapabilityGroup[](axios 拦截器自动解包 {code,data,msg})
    getCapabilities: () => axios.get('/system/capabilities'),
    // 角色当前 capability id 列表(回显)
    getRoleCapabilities: (roleId: string) =>
        axios.get(`/system/role/${roleId}/capabilities`),
    // 保存:替换角色全部 capability(后端展开码派生 role_permission)
    assignRoleCapabilities: (roleId: string, capabilityIds: string[]) =>
        axios.post(`/system/role/${roleId}/assign-capabilities`, { capability_ids: capabilityIds }),
};

// Department API
export const deptApi = {
    getDeptLookup: (params: any) => axios.post("/system/dept/lookup", params),
    searchDept: (params: any) => axios.post("/system/dept/search", params),
    createDept: (data: any) => axios.post("/system/dept", data),
    updateDept: (id: string, data: any) => axios.put(`/system/dept/${id}`, data),
    deleteDept: (id: string) => axios.delete(`/system/dept/${id}`),
};

// Attachment API
export const attachmentApi = {
    uploadFile: (formData: FormData) =>
        axios.post(`/pms/attachments/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    requestPresignUrl: (params: any) =>
        axios.post(`/pms/attachments/request-presign-url`, params),
    commitAttachment: (params: any) =>
        axios.post(`/pms/attachments/commit`, params),
    entityLinkAttachment: (params: any) =>
        axios.post(`/pms/attachments/entity-link`, params),
    getAttachment: (params: any) =>
        axios.get(`/pms/attachments/by-entities`, {
            params,
            paramsSerializer: (p: any) => {
                const searchParams = new URLSearchParams();
                Object.entries(p).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((v) => searchParams.append(key, String(v)));
                    } else if (value !== undefined && value !== null) {
                        searchParams.append(key, String(value));
                    }
                });
                return searchParams.toString();
            },
        }),
    delAttachment: (
        entity_type: string,
        entity_id: string,
        attachment_id: string,
    ) =>
        axios.delete(
            `/pms/attachments/entity-link/${entity_type}/${entity_id}/${attachment_id}`,
        ),
    /**
     * Update one attachment's link to one entity — album position (`seqNo`),
     * category, caption. `seqNo` is per-link, so re-ordering an album never
     * touches the same photo's position in another entity's album.
     *
     * "Set as cover" = give the photo a seqNo lower than every other one in
     * that album. Do NOT hardcode 0: a second set-cover would tie with the
     * first and lose the id tie-break, silently doing nothing.
     */
    updateEntityLink: (
        entity_type: string,
        entity_id: string,
        attachment_id: string,
        body: { seqNo?: number; categoryCode?: string; caption?: string },
    ) =>
        axios.put(
            `/pms/attachments/entity-link/${entity_type}/${entity_id}/${attachment_id}`,
            body,
        ),
    /**
     * Hard-delete an attachment record. `physicalDelete: true` also removes
     * the S3 object — use for orphan cleanup (attachment was uploaded but
     * never linked to any entity). Backend route: DELETE /pms/attachments/{id}.
     * Permission: pms:attachment:delete.
     */
    deleteById: (
        attachment_id: string,
        opts?: { physicalDelete?: boolean },
    ) =>
        axios.delete(`/pms/attachments/${attachment_id}`, {
            params: { physical_delete: opts?.physicalDelete ?? false },
        }),
};

// Conversations API
export const conversationsApi = {
    search: (data: any) => axios.post("/pms/conversations/search", data),

    // 动作端点 (INBOX_BACKEND_INTEGRATION_GUIDE §5)。batch body 用 camelCase conversationIds。
    batchMarkRead: (conversationIds: string[]) =>
        axios.post("/pms/conversations/batch-mark-read", { conversationIds }),
    batchMarkUnread: (conversationIds: string[]) =>
        axios.post("/pms/conversations/batch-mark-unread", { conversationIds }),
    /**
     * 按当前列表范围(view filters + 搜索词)整体标已读。服务端选行,前端不必先
     * 拉全量 id。body 的 filters 与 POST /pms/conversations/search 的 filters 同构
     * (不带 page/pageSize/orderBy/columns)。
     */
    markAllRead: (payload: { filters?: Record<string, any>; q?: string }) =>
        axios.post("/pms/conversations/mark-all-read", payload) as Promise<{ updated: number }>,
    archive: (id: string) => axios.post(`/pms/conversations/${id}/archive`),
    unarchive: (id: string) => axios.post(`/pms/conversations/${id}/unarchive`),
    setFollowUp: (id: string, followUpAt: string) =>
        axios.post(`/pms/conversations/${id}/follow-up`, { followUpAt }),
    clearFollowUp: (id: string) =>
        axios.delete(`/pms/conversations/${id}/follow-up`),
    // 单一 assignee 端点 (本期 UI 不用,先备着,见 spec D4)
    assign: (id: string, assigneeId: string) =>
        axios.post(`/pms/conversations/${id}/assign`, { assigneeId }),
    unassign: (id: string) => axios.post(`/pms/conversations/${id}/unassign`),
};

// User Entity Links API
// Read 操作统一走 search();write 保留 create / deleteByCriteria 通用写入。
// 旧 getByUser / getByEntity 已删除 —— 等价 search() 调用见 docs/SEARCH_AND_LIST_PARAMETER_GUIDE.md
// 或 OpenSpec change redesign-watchlist-via-views。
export const userEntityLinksApi = {
    /**
     * 标准 DSL 搜索端点 —— 取代旧的 getByUser / getByEntity / getMyWatches
     * POST /pms/user-entity-links/search
     */
    search: (params: any) =>
        axios.post("/pms/user-entity-links/search", params),

    /**
     * Create a new user entity link (assign user to entity)
     * POST /pms/user-entity-links
     * @param data - Link data including userId, entityType, entityId, linkType, linkDescription
     */
    create: (data: {
        userId: string;
        entityType: string;
        entityId: string;
        linkType: string;
        linkDescription?: string;
    }) => axios.post("/pms/user-entity-links", data),

    /**
     * Delete user entity link by criteria
     * DELETE /pms/user-entity-links/by-criteria
     * @param params - Delete criteria including user_id, entity_type, entity_id, link_type (as query parameters)
     */
    deleteByCriteria: (params: {
        user_id: string;
        entity_type: string;
        entity_id: string;
        link_type?: string;
    }) => axios.delete("/pms/user-entity-links/by-criteria", { params }),
};

// Notification API
export const notificationApi = {
    getNotificationMeta: (data: any) =>
        axios.get("/system/notifications/meta", data),
    getNotificationSubscriber: (id: any) =>
        axios.get(`/system/notifications/subscribers/${id}`),
    updateNotificationSubscriber: (id: any, data: any) =>
        axios.patch(
            `/system/notifications/subscribers/${id}/preferences`,
            data,
        ),
};

// Messages API
export const messagesApi = {
    search: (data: any) => axios.post("/pms/messages/search", data),
    sendMessage: (data: any) => axios.post("/pms/messages", data),
    update: (id: string, data: any) => axios.put(`/pms/messages/${id}`, data),
    // ⚠️ 这两个端点 body 用 snake_case conversation_id。INBOX_BACKEND_INTEGRATION_GUIDE §5.3
    //   写的是 camelCase,但 live 后端 /messages/send 实际要求 conversation_id(422 实测),
    //   且原有代码本就是 snake —— 指南这条与实际端点不符,以端点为准。
    /** ai_draft_id：回复框内容来自 AI 草稿（`POST /pms/messages/{id}/ai-draft` 的 draftId）时带上，后端只做比对、不拒发 */
    send: (data: { conversation_id: string; content: string; ai_draft_id?: SnowflakeId | string }) =>
        axios.post("/pms/messages/send", data),
    /**
     * 对一条入站客人消息起草回复（AI 回复草稿设计 §5.1）。权限 pms:message:send_external；同步，后端 60 秒封顶。
     * body：ticketId（从诉求条点的就传，后端只读它的 type 和事项）、language（客服界面语言，notes 用它写）。
     * 错误 code 在顶层 error_code：409 CONFLICT_AI_DRAFT_IN_PROGRESS、504 AI_DRAFT_TIMEOUT、502 AI_DRAFT_FAILED、
     * 422 AI_DRAFT_NOT_GUEST_MESSAGE / AI_DRAFT_TICKET_MISMATCH。
     */
    aiDraft: (messageId: SnowflakeId, body: { ticketId?: SnowflakeId; language?: 'en' | 'zh' | 'pt' | 'es' }) =>
        axios.post(`/pms/messages/${messageId}/ai-draft`, body) as unknown as Promise<{
            draftId: SnowflakeId | null
            status: 'drafted' | 'not_drafted'
            text: string | null
            notes: Array<{ kind: string; message: string }>
            answersMessageId: SnowflakeId
            generatedAt: string
            latencyMs: number
        }>,
    sendInternalNote: (data: { conversation_id: string; content: string }) =>
        axios.post("/pms/messages/send-internal-note", data),
    /**
     * 重发一条发送失败的出站消息 —— 原地重试,不新建行(同一条 message id)。
     * 后端权限码 pms:message:send_external。
     *
     * body.channel_code 可选:不传沿用该行原 channel_code;传了则换渠道重发(内部备注忽略)。
     * 200 返回同一条 MessageOutput(camelCase,拦截器已解包),关键字段
     * deliveryStatusCode(sent / failed)、externalMessageId、deliveryError、channelCode、sentAt。
     * 404 消息不存在;400 不是可重试的出站消息;422 channel_code 非法;
     * 409 CONFLICT_MESSAGE_NOT_RETRYABLE(已不是 failed)/ CONFLICT_MESSAGE_ALREADY_DELIVERED(同内容已送达)。
     */
    retry: (id: string, data?: { channel_code?: string }) =>
        axios.post(`/pms/messages/${id}/retry`, data ?? {}),
    /** 删除一条消息 —— 后端权限码 pms:message:delete,返回 { success: true } */
    delete: (id: string) => axios.delete(`/pms/messages/${id}`),
};

// Activity Log API
export const activityLogApi = {
    getLogs: (params: any) => axios.get(`/pms/activity-logs`, { params }),
    searchLogs: (params: any) =>
        axios.post(`/pms/activity-logs/search`, params),
};

// Attendance API
export const attendanceApi = {
    /**
     * Search attendance rows. Body 直接转发 BaseQuery shape.
     * 详见 docs/SEARCH_AND_LIST_PARAMETER_GUIDE.md.
     *
     * 典型用法:
     *   - Daily 列表: filter attendanceDate==X, columns 带 user.* 嵌套字段
     *   - Timesheet detail per-day: filter userId==X & attendanceDate between [s,e]
     */
    searchAttendance: (data: any) => axios.post(`/pms/attendance/search`, data),

    /**
     * GET 单条 attendance. params.load_relationships 接 string[] (axios 自动序列化).
     * 推荐值: ['sessions', 'user', 'creator', 'updater']
     */
    getAttendanceById: (
        id: string,
        params?: { load_relationships?: string[] },
    ) =>
        axios.get(`/pms/attendance/${id}`, {
            params,
            paramsSerializer: (p: any) => {
                const sp = new URLSearchParams();
                Object.entries(p).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((v) => sp.append(key, String(v)));
                    } else if (value !== undefined && value !== null) {
                        sp.append(key, String(value));
                    }
                });
                return sp.toString();
            },
        }),

    // Actions (不变, 后端 endpoint 未变)
    clockIn: (userId: string, body: { actionTime: string; notes?: string }) =>
        axios.post(`/pms/attendance/${userId}/actions/clock-in`, body),
    clockOut: (userId: string, body: { actionTime: string; notes?: string }) =>
        axios.post(`/pms/attendance/${userId}/actions/clock-out`, body),
    startBreak: (userId: string, body: { actionTime: string; notes?: string }) =>
        axios.post(`/pms/attendance/${userId}/actions/start-break`, body),
    endBreak: (userId: string, body: { actionTime: string; notes?: string }) =>
        axios.post(`/pms/attendance/${userId}/actions/end-break`, body),
};

// Activity Log API
export const locationTrackingApi = {
    searchLocationTracking: (data: any) =>
        axios.post("/pms/location-tracking/search", data),
};

// Lookup Table API
export const lookupTableApi = {
    getLookupTable: (code: string, includeExtra: boolean = true, includeDisabled: boolean = false) =>
        axios.get(
            `/system/lookup-table/tables/${code}?include_extra=${includeExtra}${includeDisabled ? '&include_disabled=true' : ''}`,
        ),
};

// Language / Translation API
export const languageApi = {
    translateBatch: (texts: string[], targetLang: string, sourceLang?: string) =>
        axios.post('/language/lang/translate/batch', {
            texts,
            target_language: targetLang,
            ...(sourceLang ? { source_language: sourceLang } : {}),
        }),
};

// Watchlist API
import type { WatchlistItem } from "@/types/watchlist";

export const watchlistApi = {
    /**
     * 关注实体
     * @param entityType 实体类型
     * @param entityId 实体ID
     *
     * watchlist 简化后不再读写 link_description(数据库列仍保留供未来其他
     * link_type 用)。后端 POST /watch/{type}/{id} 接受空 body,写入
     * link_description = NULL。
     */
    watchEntity: (entityType: string, entityId: string) => {
        return axios.post<WatchlistItem>(
            `/pms/user-entity-links/watch/${entityType}/${entityId}`,
            "",
            {
                headers: {
                    "Content-Type": "text/plain",
                },
            },
        );
    },

    /**
     * 取消关注实体
     * @param entityType 实体类型
     * @param entityId 实体ID
     */
    unwatchEntity: (entityType: string, entityId: string) => {
        return axios.delete(
            `/pms/user-entity-links/unwatch/${entityType}/${entityId}`,
        );
    },

    /**
     * 按类型批量删除关注项
     * @param entityType 实体类型
     */
    deleteMyWatchesByType: (entityType: string) => {
        return axios.delete("/pms/user-entity-links/my-watches", {
            params: { entity_type: entityType },
        });
    },

    /**
     * 清空所有关注项
     */
    clearAllWatches: () => {
        return axios.delete("/pms/user-entity-links/my-watches");
    },

    /**
     * @deprecated watchlist 简化后不再读写 link_description。后端端点保留
     * 但前端无调用。如需修改 link_description(其他 link_type 用例),
     * 改用通用 userEntityLinksApi.create + deleteByCriteria 重建。
     */
    updateWatchlistItem: (id: string, data: { link_description?: string }) => {
        return axios.put<WatchlistItem>(`/pms/user-entity-links/${id}`, {
            link_description: data.link_description,
        });
    },
};

// GuestGuide API
export const guestGuidesApi = {
    getGuestGuidesForListing: (listingId: string) =>
        axios.get<any, GuestGuideApiResponse[]>(`/pms/guestguides/for-listing/${listingId}`),
    getGuestGuide: (id: string) =>
        axios.get<any, GuestGuideApiResponse>(`/pms/guestguides/${id}`),
    createGuestGuide: (data: GuestGuideUpdateRequest) =>
        axios.post<any, GuestGuideApiResponse>("/pms/guestguides", data),
    updateGuestGuide: (id: string, data: GuestGuideUpdateRequest) =>
        axios.put<any, GuestGuideApiResponse>(`/pms/guestguides/${id}`, data),
    deleteGuestGuide: (id: string) =>
        axios.delete(`/pms/guestguides/${id}`),
    /**
     * AI 翻译 guide 正文为 es/pt/zh，**不落库** —— 结果填进表单供 host 审校后随表单保存。
     * 走 LLM(guestguide/translate_guide)而非通用 /language 机翻通道：guide 正文是
     * Markdown/HTML，机翻会破坏标签与价格数字。
     * 收正文而非 guide id：新建的 guide 保存前没有 id，而那正是最需要翻译的时刻。
     */
    translateGuestGuide: (data: {
        title?: string
        shortDescription?: string
        content?: string
        isHtmlContent?: boolean
    }) =>
        axios.post<any, {
            titleI18n: Record<string, string>
            shortDescriptionI18n: Record<string, string>
            contentI18n: Record<string, string>
        }>("/pms/guestguides/translate", data),
    // Global settings for guest guide
    getGlobalSettings: () =>
        axios.get<any, { settings: Record<string, any> }>("/pms/guestguides/global-setting"),
    updateGlobalSettings: (settings: Record<string, any>) =>
        axios.post<any, { settings: Record<string, any> }>("/pms/guestguides/global-setting", { settings }),
};

// Reservation Actions API
export const reservationActionsApi = {
    searchReservationActions: (data: any) =>
        axios.post<any, SearchResponse<ReservationAction>>("/pms/reservation-actions/search", data),
    getReservationAction: (id: string) =>
        axios.get(`/pms/reservation-actions/${id}`),
    /** 按 reservation 取全部 action（裸列表，不分页）。actionType 可选过滤。
     *  注意：后端 query 参数是 snake_case（reservation_id / action_type），与仓库 GET 约定一致。 */
    listByReservation: (reservationId: SnowflakeId, actionType?: string) =>
        axios.get<any, ReservationAction[]>('/pms/reservation-actions/', {
            params: { reservation_id: reservationId, ...(actionType ? { action_type: actionType } : {}) },
        }),
};

// Export History API
export const exportHistoryApi = {
    /**
     * 触发导出任务
     * @param request 导出请求参数
     */
    triggerExport: (request: TriggerExportRequest) => {
        return axios.post<ExportHistoryOutput>(
            "/pms/export-history/trigger-export",
            request,
        );
    },
    /**
     * 获取导出历史列表
     * @param params 查询参数，支持分页和搜索
     */
    getExportHistory: (params?: GetExportHistoryParams) => {
        const queryParams = new URLSearchParams();
        if (params) {
            if (params.page !== undefined) {
                queryParams.append("page", String(params.page));
            }
            if (params.pageSize !== undefined) {
                queryParams.append("pageSize", String(params.pageSize));
            }
            if (params.q) {
                queryParams.append("q", params.q);
            }
            if (params.search) {
                queryParams.append("q", params.search);
            }
            if (params.orderBy) {
                queryParams.append("orderBy", params.orderBy);
            }
            if (params.columns) {
                queryParams.append("columns", params.columns);
            }
        }
        const queryString = queryParams.toString();
        const url = `/pms/export-history${queryString ? `?${queryString}` : ""}`;
        return axios.get<ExportHistoryListResponse>(url);
    },
};

// Asset Manager API
export const assetMgrApi = {
    // Products
    searchProducts: (data: any) => axios.post("/asset-mgr/products/search", data),
    getProduct: (id: string) => axios.get(`/asset-mgr/products/${id}`),
    // Assets
    searchAssets: (data: any) => axios.post("/asset-mgr/assets/search", data),
    getAsset: (id: string) => axios.get(`/asset-mgr/assets/${id}`),
    // Categories
    searchCategories: (data: any) => axios.post("/asset-mgr/categories/search", data),
    getCategoryTree: (params?: { itemType?: string; trackingMode?: string }) =>
        axios.get("/asset-mgr/categories/tree", { params }),
    // Inventory balance (for stock check)
    searchInventoryBalance: (data: any) => axios.post("/asset-mgr/inventory/balances/search", data),
};

// Products API
export const productsApi = {
  search: (params: any) =>
    axios.post('/asset-mgr/products/search', params),
  list: (params?: any) =>
    axios.get('/asset-mgr/products', { params }),
  /** Lightweight lookup: id+name+sku only, isActive=true, no relationships.
   *  Designed for search-as-you-type dropdowns: caller passes `q` (search query)
   *  and `pageSize` (default 20), then loads more on scroll/typing. */
  lookup: (params?: { q?: string; pageSize?: number; page?: number }) =>
    axios.post('/asset-mgr/products/search', {
      q: params?.q,
      // 选品：严格命中优先，不够一页时补同义词、多语、拼错（只在第 1 页补）
      ...(params?.q?.trim() ? { qMode: 'pick' } : {}),
      filters: { isActive: { type: 'boolean', operator: 'equals', value: true } },
      columns: 'id name sku',
      // 有 q 时不传 orderBy：后端按 q 相关度排（名字命中的排在只有描述提到的前面）。
      // 显式 orderBy 会盖掉相关度，所以只在空搜索时按名字排。
      ...(params?.q?.trim() ? {} : { orderBy: 'name' }),
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 20,
    }),
  get: (id: string, params?: any) =>
    axios.get(`/asset-mgr/products/${id}`, { params }),
  /**
   * 详情页用：`GET /asset-mgr/products/{id}` 不带标签（后端只加载 identifiers / category，
   * `load_relationships` 不起作用），标签从 search 投影补上。详情里改标签是整组覆盖，
   * 不补的话会把「pending review」等看不见的标签一起删掉。补标签失败时退回 GET 的结果（tags 为空，同改动前）。
   */
  getDetail: async (id: string) => {
    const [product, tagged] = await Promise.all([
      axios.get(`/asset-mgr/products/${id}`) as Promise<any>,
      (axios.post('/asset-mgr/products/search', {
        filters: { id: { type: 'string', operator: 'equals', value: id } },
        columns: 'id tags.id tags.name tags.color',
        includeInactive: true,
        page: 1,
        pageSize: 1,
      }) as Promise<any>).catch(() => null),
    ])
    const tags = tagged?.results?.[0]?.tags
    return Array.isArray(tags) ? { ...product, tags } : product
  },
  create: (data: any) =>
    axios.post('/asset-mgr/products', data),
  update: (id: string, data: any) =>
    axios.put(`/asset-mgr/products/${id}`, data),
  delete: (id: string) =>
    axios.delete(`/asset-mgr/products/${id}`),
  setTags: (id: string, tagIds: string[]) =>
    axios.put(`/asset-mgr/products/${id}/tags`, { tagIds }),
  listIdentifiers: (productId: string) =>
    axios.get(`/asset-mgr/products/${productId}/identifiers`),
  addIdentifier: (productId: string, data: { codeType: string; codeValue: string }) =>
    axios.post(`/asset-mgr/products/${productId}/identifiers`, data),
  deleteIdentifier: (productId: string, identifierId: string) =>
    axios.delete(`/asset-mgr/products/${productId}/identifiers/${identifierId}`),
  /** 搜不到时问目录：回 `{ status: matched | ask | new, product, candidates, suggestedCategory, unavailable }`。会调模型，几秒。 */
  suggest: (text: string, signal?: AbortSignal) =>
    axios.post('/asset-mgr/products/suggest', { text }, { signal }),
  /** 分析产品图片（添加物料改版）：照片 / 截图 / 链接 / 原话 → 名称、多语名、描述、规格、分类、包装、价格。几秒。 */
  analyze: (body: { text: string; images: Array<{ url: string; kind: 'photo' | 'screenshot' }>; links: string[] }, signal?: AbortSignal) =>
    axios.post('/asset-mgr/products/analyze', body, { signal, timeout: 60_000 }),
  /** 新建前查重（Jev，约 1 秒）：回 `{ status: matched | ask | none, product, candidates, unavailable }`。 */
  duplicateCheck: (body: { name: string; text: string; categoryId: string | null; notThese: string[] }, signal?: AbortSignal) =>
    axios.post('/asset-mgr/products/duplicate-check', body, { signal, timeout: 30_000 }),
  /** 用户在候选里选了这个产品：记住他说的话。回 `{ saved, suggested, skipped }`。 */
  rememberAlias: (productId: string, text: string) =>
    axios.post(`/asset-mgr/products/${productId}/aliases`, { text }),
  /** 扫描疑似重复分组。规则法，不走 AI。 */
  duplicateCandidates: (params?: { limit?: number }) =>
    axios.get('/asset-mgr/products/duplicate-candidates', { params }),
  /** 试算合并：冲突字段 + 逐表影响面，不写数据。 */
  mergePreview: (primaryProductId: string, mergedProductIds: string[]) =>
    axios.post('/asset-mgr/products/merge/preview', {
      primaryProductId,
      mergedProductIds,
    }),
  /** 执行合并。baseUnit 不一致时必须传 confirmUnitMismatch。 */
  merge: (payload: {
    primaryProductId: string
    mergedProductIds: string[]
    fieldOverrides?: Record<string, unknown>
    confirmUnitMismatch?: boolean
  }) => axios.post('/asset-mgr/products/merge', payload),
  /** 撤销一次合并。 */
  mergeUndo: (mergeLogId: string) =>
    axios.post(`/asset-mgr/products/merge/${mergeLogId}/undo`),
  /**
   * 加采购规格（UoM v4.1）。`units ≥ 2`；同数已有就启用它并返回它（不报错）。
   * `name` 空 → 后端照已有规格叫法取名。
   */
  addPurchaseOption: (productId: string, body: { units: number; name?: string | null; vendorId?: string | null }) =>
    axios.post(`/asset-mgr/products/${productId}/purchase-options`, body)
      .finally(() => invalidateProductPackInfo(productId)),
  /** 改名、停用。`units` 不许改（改数 = 新建一条）。 */
  updatePurchaseOption: (optionId: string, body: { name?: string; isActive?: boolean }) =>
    // 只知道规格 id、不知道属于哪个产品：清全部
    axios.patch(`/asset-mgr/purchase-options/${optionId}`, body)
      .finally(() => invalidateProductPackInfo()),
}

/** 链接：按域名预览供应商名（后端 `link_vendor` 是唯一实现，前端不另写一份）。 */
export const linksApi = {
  previewVendor: (url: string, signal?: AbortSignal) =>
    axios.post('/common/links/vendor', { url }, { signal }) as Promise<{ vendor: string | null }>,
}

export const inventoryBalancesApi = {
  search: (params: any) =>
    axios.post('/asset-mgr/inventory/balances/search', params),
  list: (params?: any) =>
    axios.get('/asset-mgr/inventory/balances', { params }),
  get: (id: string, params?: any) => {
    // Serialize `load_relationships` as repeated query keys
    // (FastAPI `List[str] = Query([])` expects `?load_relationships=a&load_relationships=b`,
    // not a single comma-joined string).
    const { load_relationships, ...rest } = params ?? {}
    const rels = Array.isArray(load_relationships)
      ? load_relationships
      : typeof load_relationships === 'string' && load_relationships
        ? load_relationships.split(',').map((s: string) => s.trim()).filter(Boolean)
        : []
    const qs = rels.map((r: string) => `load_relationships=${encodeURIComponent(r)}`).join('&')
    const url = qs ? `/asset-mgr/inventory/balances/${id}?${qs}` : `/asset-mgr/inventory/balances/${id}`
    return axios.get(url, { params: rest })
  },
  update: (id: string, data: any) =>
    axios.put(`/asset-mgr/inventory/balances/${id}`, data),
  setTags: (id: string, tagIds: string[]) =>
    axios.put(`/asset-mgr/inventory/balances/${id}/tags`, { tagIds }),
}

export const inventoryActionsApi = {
  inbound:    (body: any) => axios.post('/asset-mgr/inventory/inbound', body),
  outbound:   (body: any) => axios.post('/asset-mgr/inventory/outbound', body),
  transfer:   (body: any) => axios.post('/asset-mgr/inventory/transfer', body),
  adjustment: (body: any) => axios.post('/asset-mgr/inventory/adjustment', body),
  importBatch:    (body: any) => axios.post('/asset-mgr/inventory/import', body),
  promoteToAsset: (body: any) => axios.post('/asset-mgr/inventory/promote-to-asset', body),
  // Low-stock reorder -> creates a procurement PO (scenario 12).
  reorder:    (body: any) => axios.post('/asset-mgr/inventory/reorder', body),
}

export const inventoryTransactionsApi = {
  search: (params: any) =>
    axios.post('/asset-mgr/inventory/transactions/search', params),
  list:   (params?: any) =>
    axios.get('/asset-mgr/inventory/transactions', { params }),
  /** Single-transaction fetch via search filter — no dedicated backend endpoint exists.
   *  `columns` explicitly lists nested name fields so the detail view can render
   *  product/location/counterpart/actor names instead of raw IDs. */
  getById: async (id: string): Promise<any> => {
    const result = (await axios.post('/asset-mgr/inventory/transactions/search', {
      filters: { id: { type: 'string', operator: 'equals', value: id } },
      columns:
        'id transactionType quantity sourceType referenceNo reason notes createdAt ' +
        'productId listingId counterpartListingId actorId ' +
        'product.name product.sku listing.nickname counterpartListing.nickname actor.fullName',
      pageSize: 1,
    })) as any
    return result?.results?.[0] ?? null
  },
}

export const assetMgrCategoriesApi = {
  list:    (params?: any) => axios.get('/asset-mgr/categories', { params }),
  tree:    (params?: any) => axios.get('/asset-mgr/categories/tree', { params }),
  get:     (id: string, params?: any) => axios.get(`/asset-mgr/categories/${id}`, { params }),
  create:  (data: any) => axios.post('/asset-mgr/categories', data),
  update:  (id: string, data: any) => axios.put(`/asset-mgr/categories/${id}`, data),
  delete:  (id: string) => axios.delete(`/asset-mgr/categories/${id}`),
}

// Task-coupled asset audit (P3). Replaces the removed auditSessionsApi.
export const auditTasksApi = {
  createTask: (body: import('@/types/asset-mgr').AuditTaskCreateInput) =>
    axios.post('/asset-mgr/audit/tasks', body),
  items: (taskId: string) =>
    axios.get(`/asset-mgr/audit/tasks/${taskId}/items`),
  snapshotItems: (
    taskId: string,
    body: { snapshotAll?: boolean; excludeAssetIds?: string[]; assetIds?: string[] },
  ) => axios.post(`/asset-mgr/audit/tasks/${taskId}/items`, body),
  submit: (taskId: string, body: import('@/types/asset-mgr').TaskAuditSubmitInput) =>
    axios.post(`/asset-mgr/audit/tasks/${taskId}/submit`, body),
  copyCandidates: (listingId: string) =>
    axios.get(`/asset-mgr/audit/listings/${listingId}/copy-candidates`),
  copyFrom: (body: import('@/types/asset-mgr').AuditCopyFromInput) =>
    axios.post('/asset-mgr/audit/copy-from', body),
}

export { shiftApi } from '@/features/shift/api'
export { shiftRequestApi } from '@/features/shift-request/api'

export const taskTemplatesApi = {
    search: (params: any) => axios.post("/pms/task-templates/search", params),
    get: (id: string) => axios.get(`/pms/task-templates/${id}`),
    create: (params: any) => axios.post("/pms/task-templates", params),
    update: (id: string, params: any) => axios.put(`/pms/task-templates/${id}`, params),
    delete: (id: string) => axios.delete(`/pms/task-templates/${id}`),
    batchDelete: (params: { ids: string[] }) => axios.post("/pms/task-templates/batch-delete", params),
    validate: (id: string) => axios.get(`/pms/task-templates/${id}/validate`),
    createFromTask: (taskId: string, params: any) => axios.post(`/pms/task-templates/from-task/${taskId}`, params),
    createFromTemplate: (templateId: string, params: { name: string; description?: string }) =>
        axios.post(`/pms/task-templates/from-template/${templateId}`, params),
}

export const workflowsApi = {
  search: (params: any) => axios.post("/pms/workflows/search", params),
  get: (id: string) =>
    axios.get(`/pms/workflows/${id}`).then(workflowResponseFromBackend),
  create: (params: any) =>
    axios.post("/pms/workflows", workflowPayloadToBackend(params)).then(workflowResponseFromBackend),
  update: (id: string, params: any) =>
    axios.put(`/pms/workflows/${id}`, workflowPayloadToBackend(params)).then(workflowResponseFromBackend),
  delete: (id: string) => axios.delete(`/pms/workflows/${id}`),
  batchDelete: (params: { ids: string[] }) => axios.post("/pms/workflows/batch-delete", params),
  // 批量启用/停用 —— 后端会同步 schedule workflow 的 cron 绑定 (不能用裸 update)。
  // confirmMaterialization：激活会物化任务时 (wouldCreate>0) 需带 true，否则后端
  // 返回 409 MATERIALIZATION_CONFIRMATION_REQUIRED。线上字段是 snake_case。
  batchSetActive: (params: { ids: string[]; isActive: boolean; confirmMaterialization?: boolean }) => {
    const { confirmMaterialization, ...rest } = params
    const body: Record<string, unknown> = { ...rest }
    if (confirmMaterialization !== undefined) body.confirm_materialization = confirmMaterialization
    return axios.post("/pms/workflows/batch-set-active", body)
  },
  execute: (id: string, params: any) =>
    axios.post(`/pms/workflows/${id}/execute`, params) as Promise<
      import("@/types/workflow").WorkflowExecuteResponse
    >,
  pause: (id: string) => axios.post(`/pms/workflows/${id}/pause`),
  resume: (id: string) => axios.post(`/pms/workflows/${id}/resume`),
  reconcilePreview: (id: string) => axios.post(`/pms/workflows/${id}/reconcile/preview`),
  // 激活前物化影响预览 (只读，无副作用)。返回 camelCase {mode, wouldCreate,
  // alreadyExists, occurrenceCount, targetCount}。wouldCreate>0 时激活需二次确认。
  materializePreview: (id: string) =>
    axios.post(`/pms/workflows/${id}/materialize/preview`) as Promise<
      import("@/types/workflow").MaterializePreviewResponse
    >,
  reconcileExecute: (id: string) => axios.post(`/pms/workflows/${id}/reconcile/execute`),
  // 场景6：克隆为草稿 (Draft, isActive:false, name = source + " (copy)")。
  clone: (id: string) =>
    axios.post(`/pms/workflows/${id}/clone`).then(workflowResponseFromBackend),
  getActionSchemas: () => axios.get("/pms/workflows/action-schemas"),
  searchRuns: (id: string, params: any) => axios.post(`/pms/workflows/${id}/runs/search`, params),
  searchAllRuns: (params: any) => axios.post("/pms/workflows/runs/search", params),
  getRunDetail: (runId: string) => axios.get(`/pms/workflows/runs/${runId}`),
  // 场景13：重试失败/卡死 run。返回新建的 WorkflowRun（camelCase，与 getRunDetail 同结构）。
  // 409 WORKFLOW_RUN_NOT_RETRYABLE 时由调用方 catch 并提示用户。
  retryRun: (runId: string) =>
    axios.post(`/pms/workflows/runs/${runId}/retry`) as Promise<
      import('@/types/workflow').WorkflowRun
    >,
  // 场景14：任务 → 来源工作流反向溯源。返回 null（手动任务 / 无血缘）或 TaskSourceWorkflow。
  sourceByTask: (taskId: string) =>
    axios.get(`/pms/workflows/by-task/${taskId}`) as Promise<
      import("@/types/workflow").TaskSourceWorkflow | null
    >,
}

// ── AI Test Chat（AI 试聊 / 影子评测）─────────────────────────────────────────
// 后端 `kms` 模块，prefix `/kms/ai-reply-tests`（设计 §3.3）。
// ⚠️ 契约不对称：**请求体 snake_case、响应体 camelCase**（search DSL 除外——它与
// 其它 `/search` 端点同款，走 camelCase 的 BaseQuery）。所以这里显式写 snake_case
// 的 body 键，不要「顺手」改成 camelCase。
export const aiReplyTestsApi = {
    /** 建会话。reservation_id 必填，title 缺省由后端拼 `<listing> · <guest>`。 */
    createSession: (body: AiReplySessionCreateInput) =>
        axios.post<any, AiReplySession>("/kms/ai-reply-tests/sessions", body),
    /** 会话列表（Search DSL / BaseQuery）。 */
    searchSessions: (params: any) =>
        axios.post("/kms/ai-reply-tests/sessions/search", params),
    /** 会话详情：含 turns[]（按 seq 升序）与 reservation 摘要。 */
    getSession: (id: SnowflakeId) =>
        axios.get<any, AiReplySessionDetail>(`/kms/ai-reply-tests/sessions/${id}`),
    /** 删除会话（204）。 */
    deleteSession: (id: SnowflakeId) =>
        axios.delete<any, void>(`/kms/ai-reply-tests/sessions/${id}`),
    /** 以客人身份提问，同步返回这一轮 turn（不流式，见设计"非目标"）。 */
    ask: (id: SnowflakeId, body: AiReplyAskInput) =>
        axios.post<any, AiReplyTurn>(`/kms/ai-reply-tests/sessions/${id}/ask`, body),
    /** 校对队列（Search DSL / BaseQuery）。 */
    searchTurns: (params: any) =>
        axios.post("/kms/ai-reply-tests/turns/search", params),
    /** 人工校对写回：approved / rejected / edited。 */
    reviewTurn: (id: SnowflakeId, body: AiReplyReviewInput) =>
        axios.post<any, AiReplyTurn>(`/kms/ai-reply-tests/turns/${id}/review`, body),
    /**
     * Inbox AI test mode：为一条**真实客人消息**生成 / 重新生成 AI 回复。
     *
     * 只看这条消息发出时刻之前的上下文（cutoff = message.sentAt），结果永远不发给客人、
     * 也不写 `pms.message`。⚠️ 与上面几个端点不同，**这个端点的请求体是 camelCase**
     * （`regenerate` / `expectedUpdatedAt`，BaseRequest 自动转 snake_case）——
     * 见计划文档「接口契约」段。
     *
     * 409：`CONFLICT_AI_REPLY_LOCKED`（已回复客人）/ `…_STALE`（本地行已过期）/
     * `…_IN_PROGRESS`（别处正在生成）；400 非客人消息；502 LLM 失败。
     */
    replyToMessage: (messageId: SnowflakeId, body: AiReplyInboxRequest) =>
        axios.post<any, InboxAiTurn>(`/kms/ai-reply-tests/messages/${messageId}/reply`, body),
};
